import globalAxios, { isAxiosError } from 'axios';
import { M as MultiRollupApiClients, c as createConfig, m as multiRollupConfig, a as index$1, I as ImxApiClients, b as imxApiConfig } from './index-C80azzXH.js';
import { I as IMXClient, s as signRaw, c as convertToSignableToken, g as generateLegacyStarkPrivateKey, a as createStarkSigner } from './index-CTcEmIj1.js';
import { s as setPassportClientId, t as track, b as trackFlow, c as trackError, u as utils, i as identify, g as getDetail, D as Detail, d as trackDuration, E as Environment } from './index-Bp8cN2wo.js';
import { UserManager, User, ErrorTimeout, ErrorResponse, InMemoryWebStorage, WebStorageStateStore } from 'oidc-client-ts';
import * as crypto from 'crypto';
import jwt_decode from 'jwt-decode';
import localForage from 'localforage';
import { Magic } from 'magic-sdk';
import { OpenIdExtension } from '@magic-ext/oidc';
import { StaticJsonRpcProvider, Web3Provider } from '@ethersproject/providers';
import { utils as utils$1, Contract, BigNumber, errors, ethers, constants } from 'ethers';
import { EventEmitter } from 'events';
import { walletContracts } from '@0xsequence/abi';
import { v1 } from '@0xsequence/core';
import { v4 } from 'uuid';

var PassportErrorType;
(function (PassportErrorType) {
    PassportErrorType["AUTHENTICATION_ERROR"] = "AUTHENTICATION_ERROR";
    PassportErrorType["INVALID_CONFIGURATION"] = "INVALID_CONFIGURATION";
    PassportErrorType["WALLET_CONNECTION_ERROR"] = "WALLET_CONNECTION_ERROR";
    PassportErrorType["NOT_LOGGED_IN_ERROR"] = "NOT_LOGGED_IN_ERROR";
    PassportErrorType["SILENT_LOGIN_ERROR"] = "SILENT_LOGIN_ERROR";
    PassportErrorType["REFRESH_TOKEN_ERROR"] = "REFRESH_TOKEN_ERROR";
    PassportErrorType["USER_REGISTRATION_ERROR"] = "USER_REGISTRATION_ERROR";
    PassportErrorType["USER_NOT_REGISTERED_ERROR"] = "USER_NOT_REGISTERED_ERROR";
    PassportErrorType["LOGOUT_ERROR"] = "LOGOUT_ERROR";
    PassportErrorType["TRANSFER_ERROR"] = "TRANSFER_ERROR";
    PassportErrorType["CREATE_ORDER_ERROR"] = "CREATE_ORDER_ERROR";
    PassportErrorType["CANCEL_ORDER_ERROR"] = "CANCEL_ORDER_ERROR";
    PassportErrorType["EXCHANGE_TRANSFER_ERROR"] = "EXCHANGE_TRANSFER_ERROR";
    PassportErrorType["CREATE_TRADE_ERROR"] = "CREATE_TRADE_ERROR";
    PassportErrorType["OPERATION_NOT_SUPPORTED_ERROR"] = "OPERATION_NOT_SUPPORTED_ERROR";
    PassportErrorType["LINK_WALLET_ALREADY_LINKED_ERROR"] = "LINK_WALLET_ALREADY_LINKED_ERROR";
    PassportErrorType["LINK_WALLET_MAX_WALLETS_LINKED_ERROR"] = "LINK_WALLET_MAX_WALLETS_LINKED_ERROR";
    PassportErrorType["LINK_WALLET_VALIDATION_ERROR"] = "LINK_WALLET_VALIDATION_ERROR";
    PassportErrorType["LINK_WALLET_DUPLICATE_NONCE_ERROR"] = "LINK_WALLET_DUPLICATE_NONCE_ERROR";
    PassportErrorType["LINK_WALLET_GENERIC_ERROR"] = "LINK_WALLET_GENERIC_ERROR";
})(PassportErrorType || (PassportErrorType = {}));
function isAPIError(error) {
    return 'code' in error && 'message' in error;
}
class PassportError extends Error {
    type;
    constructor(message, type) {
        super(message);
        this.type = type;
    }
}
const withPassportError = async (fn, customErrorType) => {
    try {
        return await fn();
    }
    catch (error) {
        let errorMessage;
        if (isAxiosError(error) && error.response?.data && isAPIError(error.response.data)) {
            errorMessage = error.response.data.message;
        }
        else {
            errorMessage = error.message;
        }
        throw new PassportError(errorMessage, customErrorType);
    }
};

/* eslint-disable class-methods-use-this */
const KEY_PKCE_STATE = 'pkce_state';
const KEY_PKCE_VERIFIER = 'pkce_verifier';
const validCredentialsMinTtlSec = 3600; // 1 hour
class DeviceCredentialsManager {
    areValid(tokenResponse) {
        if (tokenResponse) {
            const accessTokenValid = this.isTokenValid(tokenResponse.access_token);
            const idTokenValid = this.isTokenValid(tokenResponse.id_token);
            return accessTokenValid && idTokenValid;
        }
        return false;
    }
    isTokenValid(jwt) {
        try {
            const tokenPayload = jwt_decode(jwt);
            const expiresAt = tokenPayload.exp ?? 0;
            const now = (Date.now() / 1000) + validCredentialsMinTtlSec;
            return expiresAt > now;
        }
        catch (error) {
            return false;
        }
    }
    savePKCEData(data) {
        localStorage.setItem(KEY_PKCE_STATE, data.state);
        localStorage.setItem(KEY_PKCE_VERIFIER, data.verifier);
    }
    getPKCEData() {
        const state = localStorage.getItem(KEY_PKCE_STATE);
        const verifier = localStorage.getItem(KEY_PKCE_VERIFIER);
        if (state && verifier) {
            return { state, verifier };
        }
        return null;
    }
}

const warn = (...args) => {
    if (typeof process === 'undefined') {
        return;
    }
    const shouldLog = process?.env?.JEST_WORKER_ID === undefined;
    if (shouldLog) {
        // eslint-disable-next-line no-console
        console.warn(...args);
    }
};
var logger = {
    warn,
};

function isIdTokenExpired(idToken) {
    if (!idToken) {
        return false;
    }
    const decodedToken = jwt_decode(idToken);
    const now = Math.floor(Date.now() / 1000);
    return decodedToken.exp < now;
}
function isTokenExpired(oidcUser) {
    const { id_token: idToken, expired } = oidcUser;
    if (expired) {
        return true;
    }
    return isIdTokenExpired(idToken);
}

var PassportEvents;
(function (PassportEvents) {
    PassportEvents["LOGGED_OUT"] = "loggedOut";
    PassportEvents["LOGGED_IN"] = "loggedIn";
    PassportEvents["ACCOUNTS_REQUESTED"] = "accountsRequested";
})(PassportEvents || (PassportEvents = {}));
const isUserZkEvm = (user) => !!user.zkEvm;
const isUserImx = (user) => !!user.imx;

/* eslint-disable max-len */
const PASSPORT_OVERLAY_ID = 'passport-overlay';
const PASSPORT_OVERLAY_CLOSE_ID = `${PASSPORT_OVERLAY_ID}-close`;
const PASSPORT_OVERLAY_TRY_AGAIN_ID = `${PASSPORT_OVERLAY_ID}-try-again`;
const CLOSE_BUTTON_SVG = `
  <svg
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style="width: 20px !important;"
    >
      <path
        d="M16.25 5.75833L14.2417 3.75L10 7.99167L5.75833 3.75L3.75 5.75833L7.99167 10L3.75 14.2417L5.75833 16.25L10 12.0083L14.2417 16.25L16.25 14.2417L12.0083 10L16.25 5.75833Z"
        fill="#F3F3F3"
      />
  </svg>
`;
const POPUP_BLOCKED_SVG = `
  <svg
  viewBox="0 0 17 16"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
  style="width: 16px !important;"
  >
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M0.5 14.3333L8.5 0.333336L16.5 14.3333H0.5ZM9.16667 10.6667V12H7.83333V10.6667H9.16667ZM9.16667 5.33334L9.16667 9.33334H7.83333L7.83333 5.33334H9.16667Z"
      fill="#E01A3D"
    />
  </svg>
`;
const IMMUTABLE_LOGO_SVG = `
  <svg
    style="
      max-width: 123px !important;
      margin-bottom: 24px !important;
    "
    viewBox="0 0 124 112"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_332_47939)">
      <g clip-path="url(#clip1_332_47939)">
        <path
          d="M4.10008 74.9453H0.5V93.6041H4.10008V74.9453Z"
          fill="#F3F3F3"
        />
        <path
          d="M22.9585 80.0212C21.1727 80.0212 19.5283 80.7622 18.5505 82.3115C17.8209 80.8013 16.3595 80.0212 14.4691 80.0212C12.864 80.0212 11.3786 80.7231 10.4792 82.1681V80.282H7.00976V93.6063H10.4792V86.2032C10.4792 84.3432 11.7445 82.9503 13.3475 82.9503H13.5565C14.9787 82.9503 15.643 83.8477 15.643 85.6187V93.6085H19.1124V86.2054C19.1124 84.3453 20.3647 82.9525 21.9676 82.9525H22.1767C23.5989 82.9525 24.237 83.8499 24.237 85.6208V93.6106H27.7064V85.2188C27.7064 83.6435 27.3274 82.3832 26.5717 81.4315C25.8029 80.4949 24.6029 80.0256 22.9585 80.0256V80.0212Z"
          fill="#F3F3F3"
        />
        <path
          d="M46.0291 80.0212C44.2432 80.0212 42.5989 80.7622 41.621 82.3115C40.8914 80.8013 39.43 80.0212 37.5396 80.0212C35.9345 80.0212 34.4492 80.7231 33.5497 82.1681V80.282H30.0803V93.6063H33.5497V86.2032C33.5497 84.3432 34.8151 82.9503 36.418 82.9503H36.6271C38.0493 82.9503 38.7135 83.8477 38.7135 85.6187V93.6085H42.1829V86.2054C42.1829 84.3453 43.4352 82.9525 45.0381 82.9525H45.2472C46.6694 82.9525 47.3075 83.8499 47.3075 85.6208V93.6106H50.7769V85.2188C50.7769 83.6435 50.398 82.3832 49.6422 81.4315C48.8734 80.4949 47.6734 80.0256 46.0291 80.0256V80.0212Z"
          fill="#F3F3F3"
        />
        <path
          d="M72.3077 89.2061V83.0785H75.2936V80.282H72.3077V75.9622H68.8383V80.282H61.4312V87.6851C61.4312 89.5451 60.192 91.0683 58.6805 91.0683H58.4715C57.2061 91.0683 56.4896 90.1318 56.4896 88.413V80.282H53.0202V88.8041C53.0202 90.3534 53.3991 91.5746 54.1549 92.4872C54.8975 93.4107 56.0322 93.867 57.5328 93.867C59.188 93.867 60.6102 93.0218 61.4312 91.655V93.6063H64.9006V83.0807H68.8405V90.1448C68.8405 92.396 70.0536 93.6063 72.3099 93.6063H75.7009V90.6794H73.7843C72.5582 90.6794 72.3099 90.4317 72.3099 89.2083L72.3077 89.2061Z"
          fill="#F3F3F3"
        />
        <path
          d="M88.0757 84.8082C88.0757 81.7378 85.8325 80.0191 82.4676 80.0191C79.1027 80.0191 77.0033 81.8791 76.7158 84.404H80.1982C80.2766 83.5979 81.0977 82.8156 82.35 82.8156H82.5722C83.8506 82.8156 84.7109 83.7391 84.7109 84.9364V85.2623L81.346 85.5622C79.9108 85.6795 78.7369 86.0945 77.8243 86.8246C76.9118 87.5525 76.4544 88.5934 76.4544 89.9471C76.4152 92.2895 78.4363 93.8888 80.8364 93.8627C82.5438 93.8627 83.9529 93.1348 84.7871 91.9766C84.8132 92.6785 84.8655 93.226 84.9439 93.6019H88.2042C88.1127 92.9783 88.0735 92.0005 88.0735 90.675V84.806L88.0757 84.8082ZM84.7109 88.1653C84.7109 89.8428 83.3148 91.0661 81.738 91.0661H81.5289C80.5772 91.0661 79.8737 90.5316 79.8737 89.7124C79.8737 89.204 80.0697 88.8281 80.4596 88.5543C80.8516 88.2805 81.3068 88.124 81.8164 88.0849L84.7109 87.785V88.1653Z"
          fill="#F3F3F3"
        />
        <path
          d="M97.7935 80.0212C96.0076 80.0212 94.5201 81.036 93.9452 82.0116V74.9475H90.4758V93.6063H93.9452V91.8766C94.5201 92.8523 96.0055 93.867 97.7935 93.867C101.537 93.9322 103.765 90.5881 103.726 86.9441C103.765 83.3002 101.535 79.956 97.7935 80.0212ZM97.2055 91.0683H96.9964C95.4044 91.1074 93.919 89.3908 93.9452 86.9441C93.919 84.4974 95.4065 82.7808 96.9964 82.8199H97.2055C98.9522 82.8199 100.257 84.4192 100.257 86.9181C100.257 89.4169 98.9391 91.0683 97.2055 91.0683Z"
          fill="#F3F3F3"
        />
        <path
          d="M108.931 74.9453H105.462V93.6041H108.931V74.9453Z"
          fill="#F3F3F3"
        />
        <path
          d="M117.057 80.0212C113.146 79.9691 110.667 82.9612 110.706 86.9441C110.641 91.1987 113.705 93.9192 117.057 93.867C120.33 93.867 122.443 92.0461 123.094 89.3908H119.651C119.403 90.3665 118.555 91.0683 117.303 91.0683H117.081C115.633 91.0683 114.2 89.8189 114.069 88.0371H123.094C123.133 87.4656 123.147 87.0484 123.147 86.7877C123.186 82.8982 120.956 79.9821 117.057 80.0212ZM114.071 85.3688C114.15 83.7934 115.363 82.8178 116.824 82.8178H117.033C118.495 82.8178 119.708 83.7934 119.784 85.3688H114.071Z"
          fill="#F3F3F3"
        />
      </g>
      <path
        d="M30.4851 101.025V109H32.0581V106.195H33.2571C35.0941 106.195 36.7221 105.7 36.7221 103.665C36.7221 101.256 34.8521 101.025 33.2131 101.025H30.4851ZM33.2461 102.257C34.1041 102.257 35.1051 102.367 35.1051 103.676C35.1051 104.732 34.3351 104.974 33.3561 104.974H32.0581V102.257H33.2461Z"
        fill="#F3F3F3"
      />
      <path
        d="M36.9683 109H38.5743L39.1353 107.383H42.2373L42.7983 109H44.5034L41.5224 101.025H39.9383L36.9683 109ZM40.6863 102.95L41.7863 106.096H39.5863L40.6863 102.95Z"
        fill="#F3F3F3"
      />
      <path
        d="M49.1875 105.689C50.0345 105.843 50.6615 106.096 50.6615 106.778C50.6615 107.636 49.7705 107.889 49.0665 107.889C48.1205 107.889 47.3065 107.537 47.1305 106.371H45.6125C45.7555 108.087 47.0535 109.143 49.0115 109.143C50.6175 109.143 52.2455 108.34 52.2455 106.701C52.2455 105.051 50.8155 104.534 49.5175 104.303L48.4725 104.116C47.8345 103.995 47.3615 103.687 47.3615 103.126C47.3615 102.411 48.1755 102.136 48.9015 102.136C49.6495 102.136 50.4635 102.444 50.5845 103.379H52.1025C52.0255 101.85 50.6175 100.882 48.9675 100.882C47.4935 100.882 45.7885 101.586 45.7885 103.192C45.7885 104.578 46.8775 105.249 48.1755 105.502L49.1875 105.689Z"
        fill="#F3F3F3"
      />
      <path
        d="M57.5244 105.689C58.3714 105.843 58.9984 106.096 58.9984 106.778C58.9984 107.636 58.1074 107.889 57.4034 107.889C56.4574 107.889 55.6434 107.537 55.4674 106.371H53.9494C54.0924 108.087 55.3904 109.143 57.3484 109.143C58.9544 109.143 60.5824 108.34 60.5824 106.701C60.5824 105.051 59.1524 104.534 57.8544 104.303L56.8094 104.116C56.1714 103.995 55.6984 103.687 55.6984 103.126C55.6984 102.411 56.5124 102.136 57.2384 102.136C57.9864 102.136 58.8004 102.444 58.9214 103.379H60.4394C60.3624 101.85 58.9544 100.882 57.3044 100.882C55.8304 100.882 54.1254 101.586 54.1254 103.192C54.1254 104.578 55.2144 105.249 56.5124 105.502L57.5244 105.689Z"
        fill="#F3F3F3"
      />
      <path
        d="M62.5544 101.025V109H64.1274V106.195H65.3264C67.1634 106.195 68.7914 105.7 68.7914 103.665C68.7914 101.256 66.9214 101.025 65.2824 101.025H62.5544ZM65.3154 102.257C66.1734 102.257 67.1744 102.367 67.1744 103.676C67.1744 104.732 66.4044 104.974 65.4254 104.974H64.1274V102.257H65.3154Z"
        fill="#F3F3F3"
      />
      <path
        d="M71.8888 105.007C71.8888 103.137 72.9228 102.136 74.1658 102.136C75.4088 102.136 76.4428 103.137 76.4428 105.007C76.4428 106.877 75.4088 107.889 74.1658 107.889C72.9228 107.889 71.8888 106.877 71.8888 105.007ZM78.0708 105.007C78.0708 102.532 76.5418 100.882 74.1658 100.882C71.7898 100.882 70.2608 102.532 70.2608 105.007C70.2608 107.482 71.7898 109.143 74.1658 109.143C76.5418 109.143 78.0708 107.482 78.0708 105.007Z"
        fill="#F3F3F3"
      />
      <path
        d="M85.0133 109H86.7623L84.9913 105.546C85.9813 105.128 86.4323 104.358 86.4323 103.445C86.4323 101.773 85.4313 101.025 82.8023 101.025H80.1843V109H81.7573V105.876H83.0553H83.4293L85.0133 109ZM82.9783 102.257C84.0453 102.257 84.8153 102.532 84.8153 103.456C84.8153 104.237 84.2763 104.655 83.0553 104.655H81.7573V102.257H82.9783Z"
        fill="#F3F3F3"
      />
      <path
        d="M90.1424 109H91.7154V102.301H94.1794V101.025H87.6894V102.301H90.1424V109Z"
        fill="#F3F3F3"
      />
      <g clip-path="url(#clip2_332_47939)">
        <circle
          cx="61.5"
          cy="30"
          r="28.125"
          fill="url(#paint0_radial_332_47939)"
        />
        <circle
          cx="61.5"
          cy="30"
          r="28.125"
          fill="url(#paint1_radial_332_47939)"
        />
        <path
          d="M61.5 0C44.9315 0 31.5 13.4315 31.5 30C31.5 46.5685 44.9315 60 61.5 60C78.0685 60 91.5 46.5685 91.5 30C91.5 13.4315 78.0685 0 61.5 0ZM60.3397 11.4576C61.1729 11.0494 62.0508 11.0774 62.8588 11.5359C65.6603 13.1323 68.4534 14.7428 71.2325 16.37C72.1272 16.8956 72.5857 17.7372 72.5885 18.7717C72.6053 22.3979 72.5997 26.0214 72.5885 29.6477C72.5885 29.7819 72.5019 29.9776 72.3928 30.0419C71.3164 30.685 70.226 31.3029 69.0433 31.9851V31.4147C69.0433 27.685 69.0322 23.9581 69.0517 20.2283C69.0545 19.5126 68.8085 19.0513 68.1738 18.6906C64.9222 16.8425 61.6873 14.9609 58.4469 13.0904C58.3071 13.0093 58.1701 12.9226 57.9576 12.794C58.7908 12.3215 59.5401 11.8462 60.3341 11.4576H60.3397ZM59.7442 48.5564C59.5624 48.4641 59.4282 48.3998 59.2968 48.3243C55.0051 45.8499 50.719 43.3588 46.4133 40.904C45.2055 40.2162 44.6547 39.2349 44.6687 37.8565C44.6938 34.9264 44.6855 31.9963 44.6715 29.0634C44.6659 27.7409 45.2027 26.7819 46.3658 26.1221C49.3882 24.4026 52.3938 22.658 55.3993 20.9105C55.6594 20.7596 55.8495 20.7344 56.1207 20.8966C57.158 21.52 58.2148 22.1156 59.3192 22.753C59.1095 22.8788 58.9501 22.9739 58.788 23.069C55.5335 24.9478 52.2847 26.8322 49.0219 28.6999C48.4879 29.0047 48.239 29.4017 48.2446 30.0224C48.2614 32.3318 48.2642 34.6412 48.2446 36.9506C48.239 37.5881 48.4935 37.9935 49.0415 38.3066C52.4832 40.2749 55.911 42.2656 59.35 44.2395C59.6407 44.4073 59.7637 44.5806 59.7582 44.9273C59.733 46.11 59.7498 47.2954 59.7498 48.5592L59.7442 48.5564ZM59.7442 41.9413C59.445 41.7707 59.2297 41.6505 59.0144 41.5247C56.2856 39.9506 53.5596 38.3709 50.8253 36.808C50.5289 36.6403 50.4199 36.4585 50.4226 36.1146C50.4422 34.3392 50.4338 32.5638 50.4282 30.7884C50.4282 30.548 50.4646 30.383 50.7022 30.2488C51.7088 29.6869 52.7041 29.0997 53.7022 28.5266C53.7749 28.4846 53.8532 28.4539 53.9846 28.3896V30.383C53.9846 31.4623 54.0014 32.5443 53.979 33.6235C53.9651 34.2386 54.2027 34.6552 54.7395 34.9571C56.2856 35.8294 57.8122 36.7353 59.3583 37.6048C59.6631 37.7754 59.7554 37.9655 59.7498 38.301C59.7302 39.4809 59.7414 40.6636 59.7414 41.9385L59.7442 41.9413ZM56.5932 18.6375C56.0144 18.2964 55.5196 18.2992 54.9408 18.6375C51.6976 20.5331 48.4404 22.4035 45.1859 24.2824C45.0489 24.3635 44.9063 24.4362 44.6631 24.5676C44.7498 23.3318 44.4842 22.1659 44.9063 21.0363C45.1356 20.4185 45.5662 19.9543 46.1365 19.6244C48.8514 18.0559 51.5634 16.4846 54.2838 14.9245C55.2763 14.3541 56.2884 14.3681 57.281 14.9413C60.3705 16.7195 63.4571 18.5033 66.5382 20.2926C66.6696 20.3681 66.8346 20.5387 66.8346 20.6673C66.8569 21.9366 66.8486 23.2088 66.8486 24.5704C65.7274 23.9245 64.6929 23.3262 63.6584 22.7279C61.3015 21.3663 58.939 20.0186 56.5932 18.6375ZM78.3033 38.5974C78.2223 39.5955 77.6379 40.3029 76.774 40.8006C73.4664 42.7018 70.1617 44.6142 66.8569 46.521C65.8197 47.1193 64.7824 47.7176 63.7423 48.3159C63.6165 48.3886 63.4879 48.4501 63.3173 48.5396C63.3061 48.3663 63.2922 48.2404 63.2922 48.1146C63.2922 47.0186 63.2978 45.9254 63.2866 44.8294C63.2866 44.5806 63.3453 44.4296 63.5746 44.2982C68.3947 41.5247 73.212 38.7428 78.0266 35.9637C78.0993 35.9217 78.1803 35.8882 78.3173 35.8239C78.3173 36.7968 78.3732 37.7027 78.3033 38.5974ZM78.3285 31.6971C78.3201 32.8322 77.7078 33.6291 76.7377 34.1883C72.9688 36.3551 69.2027 38.5331 65.4366 40.7083C64.7488 41.1053 64.0582 41.4995 63.3201 41.9245C63.3089 41.7372 63.295 41.6142 63.295 41.4883C63.295 40.4091 63.3062 39.3271 63.2866 38.2479C63.281 37.9404 63.3844 37.7782 63.65 37.6244C67.075 35.6589 70.4888 33.671 73.9194 31.7167C74.5289 31.37 74.7973 30.9422 74.7917 30.2265C74.7665 26.4967 74.7805 22.767 74.7805 19.0373V18.3858C75.6752 18.9254 76.514 19.37 77.2801 19.9124C77.979 20.4073 78.3313 21.1566 78.3341 22.0205C78.3453 25.247 78.3537 28.4734 78.3313 31.6999L78.3285 31.6971Z"
          fill="#131313"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M61.5 0C44.9315 0 31.5 13.4315 31.5 30C31.5 46.5685 44.9315 60 61.5 60C78.0685 60 91.5 46.5685 91.5 30C91.5 13.4315 78.0685 0 61.5 0ZM61.5 2.5C46.3122 2.5 34 14.8122 34 30C34 45.1878 46.3122 57.5 61.5 57.5C76.6878 57.5 89 45.1878 89 30C89 14.8122 76.6878 2.5 61.5 2.5Z"
          fill="url(#paint2_radial_332_47939)"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M61.5 0C44.9315 0 31.5 13.4315 31.5 30C31.5 46.5685 44.9315 60 61.5 60C78.0685 60 91.5 46.5685 91.5 30C91.5 13.4315 78.0685 0 61.5 0ZM61.5 2.5C46.3122 2.5 34 14.8122 34 30C34 45.1878 46.3122 57.5 61.5 57.5C76.6878 57.5 89 45.1878 89 30C89 14.8122 76.6878 2.5 61.5 2.5Z"
          fill="url(#paint3_radial_332_47939)"
        />
      </g>
    </g>
    <defs>
      <radialGradient
        id="paint0_radial_332_47939"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(48.3053 16.7373) rotate(44.9817) scale(58.4359 123.929)"
      >
        <stop stop-color="#A3EEF8" />
        <stop offset="0.177083" stop-color="#A4DCF5" />
        <stop offset="0.380208" stop-color="#A6AEEC" />
        <stop offset="1" stop-color="#ECBEE1" />
      </radialGradient>
      <radialGradient
        id="paint1_radial_332_47939"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(63.9394 54.6335) rotate(84.265) scale(30.2672 57.9018)"
      >
        <stop stop-color="#FCF5EE" />
        <stop offset="0.715135" stop-color="#ECBEE1" stop-opacity="0" />
      </radialGradient>
      <radialGradient
        id="paint2_radial_332_47939"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(47.4257 15.8532) rotate(44.9817) scale(62.3316 132.191)"
      >
        <stop stop-color="#A3EEF8" />
        <stop offset="0.177083" stop-color="#A4DCF5" />
        <stop offset="0.380208" stop-color="#A6AEEC" />
        <stop offset="1" stop-color="#ECBEE1" />
      </radialGradient>
      <radialGradient
        id="paint3_radial_332_47939"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(64.102 56.2758) rotate(84.265) scale(32.2851 61.7619)"
      >
        <stop stop-color="#FCF5EE" />
        <stop offset="0.715135" stop-color="#ECBEE1" stop-opacity="0" />
      </radialGradient>
      <clipPath id="clip0_332_47939">
        <rect
          width="123"
          height="112"
          fill="white"
          transform="translate(0.5)"
        />
      </clipPath>
      <clipPath id="clip1_332_47939">
        <rect
          width="123"
          height="19"
          fill="white"
          transform="translate(0.5 75)"
        />
      </clipPath>
      <clipPath id="clip2_332_47939">
        <rect
          width="60"
          height="60"
          fill="white"
          transform="translate(31.5)"
        />
      </clipPath>
    </defs>
  </svg>
`;

const getCloseButton = () => `
    <button
      id="${PASSPORT_OVERLAY_CLOSE_ID}"
      style="
        background: #f3f3f326 !important;
        border: none !important;
        border-radius: 50% !important;
        width: 48px !important;
        height: 48px !important;
        position: absolute !important;
        top: 40px !important;
        right: 40px !important;
        cursor: pointer !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      "
    >
      ${CLOSE_BUTTON_SVG}
    </button>
  `;
const getBlockedContents = () => `
    <div
      style="
        color: #e01a3d !important;
        display: flex !important;
        align-items: center !important;
        gap: 4px !important;
        margin-bottom: 10px !important;
      "
    >
      ${POPUP_BLOCKED_SVG}
      Pop-up blocked
    </div>
    <p style="
        color: #b6b6b6 !important;
        text-align: center !important;
        margin: 0 !important;
      "
    >
      Please try again below.<br />
      If the problem continues, adjust your<br />
      browser settings.
    </p>
  `;
const getGenericContents = () => `
    <p style="
        color: #b6b6b6 !important;
        text-align: center !important;
        margin: 0 !important;
      "
    >
      Secure pop-up not showing?<br />We'll help you re-launch
    </p>
  `;
const getTryAgainButton = () => `
    <button
      id="${PASSPORT_OVERLAY_TRY_AGAIN_ID}"
      style="
        margin-top: 27px !important;
        color: #f3f3f3 !important;
        background: transparent !important;
        padding: 12px 24px !important;
        border-radius: 30px !important;
        border: 2px solid #f3f3f3 !important;
        font-size: 1em !important;
        font-weight: 500 !important;
        cursor: pointer !important;
      "
    >
      Try again
    </button>
  `;
const getOverlay = (contents) => `
    <div
      id="${PASSPORT_OVERLAY_ID}"
      style="
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background: rgba(13, 13, 13, 0.48) !important;
        backdrop-filter: blur(28px) !important;
        -webkit-backdrop-filter: blur(28px) !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        align-items: center !important;
        font-size: 16px !important;
        line-height: 1.5 !important;
        font-family: Roboto !important;
        font-style: normal !important;
        font-weight: 400 !important;
        font-feature-settings: 'clig' off, 'liga' off !important;
        z-index: 2147483647 !important;
      "
    >
      ${getCloseButton()}
      <div
        style="
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          max-width: 400px !important;
        "
      >
        ${IMMUTABLE_LOGO_SVG}
        ${contents}
        ${getTryAgainButton()}
      </div>
    </div>
  `;
function addLink({ id, href, rel, crossOrigin, }) {
    const fullId = `${PASSPORT_OVERLAY_ID}-${id}`;
    if (!document.getElementById(fullId)) {
        const link = document.createElement('link');
        link.id = fullId;
        link.href = href;
        if (rel)
            link.rel = rel;
        if (crossOrigin)
            link.crossOrigin = crossOrigin;
        document.head.appendChild(link);
    }
}
const getBlockedOverlay = () => getOverlay(getBlockedContents());
const getGenericOverlay = () => getOverlay(getGenericContents());

class Overlay {
    disableGenericPopupOverlay;
    disableBlockedPopupOverlay;
    overlay;
    isBlockedOverlay;
    tryAgainListener;
    onCloseListener;
    constructor(popupOverlayOptions, isBlockedOverlay = false) {
        this.disableBlockedPopupOverlay = popupOverlayOptions.disableBlockedPopupOverlay || false;
        this.disableGenericPopupOverlay = popupOverlayOptions.disableGenericPopupOverlay || false;
        this.isBlockedOverlay = isBlockedOverlay;
    }
    append(tryAgainOnClick, onCloseClick) {
        if (this.shouldAppendOverlay()) {
            this.appendOverlay();
            this.updateTryAgainButton(tryAgainOnClick);
            this.updateCloseButton(onCloseClick);
        }
    }
    update(tryAgainOnClick) {
        this.updateTryAgainButton(tryAgainOnClick);
    }
    remove() {
        if (this.overlay) {
            this.overlay.remove();
        }
    }
    shouldAppendOverlay() {
        if (this.disableGenericPopupOverlay && this.disableBlockedPopupOverlay)
            return false;
        if (this.disableGenericPopupOverlay && !this.isBlockedOverlay)
            return false;
        if (this.disableBlockedPopupOverlay && this.isBlockedOverlay)
            return false;
        return true;
    }
    appendOverlay() {
        if (!this.overlay) {
            addLink({ id: 'link-googleapis', href: 'https://fonts.googleapis.com' });
            addLink({ id: 'link-gstatic', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' });
            addLink({ id: 'link-roboto', href: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&display=swap', rel: 'stylesheet' });
            const overlay = document.createElement('div');
            overlay.innerHTML = this.isBlockedOverlay ? getBlockedOverlay() : getGenericOverlay();
            document.body.insertAdjacentElement('beforeend', overlay);
            this.overlay = overlay;
        }
    }
    updateTryAgainButton(tryAgainOnClick) {
        const tryAgainButton = document.getElementById(PASSPORT_OVERLAY_TRY_AGAIN_ID);
        if (tryAgainButton) {
            if (this.tryAgainListener) {
                tryAgainButton.removeEventListener('click', this.tryAgainListener);
            }
            this.tryAgainListener = tryAgainOnClick;
            tryAgainButton.addEventListener('click', tryAgainOnClick);
        }
    }
    updateCloseButton(onCloseClick) {
        const closeButton = document.getElementById(PASSPORT_OVERLAY_CLOSE_ID);
        if (closeButton) {
            if (this.onCloseListener) {
                closeButton.removeEventListener('click', this.onCloseListener);
            }
            this.onCloseListener = onCloseClick;
            closeButton.addEventListener('click', onCloseClick);
        }
    }
}

class LocalForageAsyncStorage {
    storage;
    constructor(name, driver) {
        this.storage = localForage.createInstance({ name, driver });
    }
    get length() {
        return this.storage.length();
    }
    clear() {
        return this.storage.clear();
    }
    getItem(key) {
        return this.storage.getItem(key);
    }
    key(index) {
        return this.storage.key(index);
    }
    async removeItem(key) {
        await this.storage.removeItem(key);
    }
    async setItem(key, value) {
        await this.storage.setItem(key, value);
    }
}

const formUrlEncodedHeader = {
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
    },
};
const logoutEndpoint = '/v2/logout';
const authorizeEndpoint = '/authorize';
const getAuthConfiguration = (config) => {
    const { authenticationDomain, oidcConfiguration } = config;
    let store;
    if (config.crossSdkBridgeEnabled) {
        store = new LocalForageAsyncStorage('ImmutableSDKPassport', localForage.INDEXEDDB);
    }
    else if (typeof window !== 'undefined') {
        store = window.localStorage;
    }
    else {
        store = new InMemoryWebStorage();
    }
    const userStore = new WebStorageStateStore({ store });
    const endSessionEndpoint = new URL(logoutEndpoint, authenticationDomain.replace(/^(?:https?:\/\/)?(.*)/, 'https://$1'));
    endSessionEndpoint.searchParams.set('client_id', oidcConfiguration.clientId);
    if (oidcConfiguration.logoutRedirectUri) {
        endSessionEndpoint.searchParams.set('returnTo', oidcConfiguration.logoutRedirectUri);
    }
    const baseConfiguration = {
        authority: authenticationDomain,
        redirect_uri: oidcConfiguration.redirectUri,
        popup_redirect_uri: oidcConfiguration.redirectUri,
        client_id: oidcConfiguration.clientId,
        metadata: {
            authorization_endpoint: `${authenticationDomain}/authorize`,
            token_endpoint: `${authenticationDomain}/oauth/token`,
            userinfo_endpoint: `${authenticationDomain}/userinfo`,
            end_session_endpoint: endSessionEndpoint.toString(),
        },
        mergeClaims: true,
        automaticSilentRenew: false, // Disabled until https://github.com/authts/oidc-client-ts/issues/430 has been resolved
        scope: oidcConfiguration.scope,
        userStore,
    };
    if (oidcConfiguration.audience) {
        baseConfiguration.extraQueryParams = {
            audience: oidcConfiguration.audience,
        };
    }
    return baseConfiguration;
};
function wait$2(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}
function base64URLEncode(str) {
    return str.toString('base64')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}
function sha256(buffer) {
    return crypto.createHash('sha256').update(buffer).digest();
}
class AuthManager {
    userManager;
    deviceCredentialsManager;
    config;
    logoutMode;
    /**
     * Promise that is used to prevent multiple concurrent calls to the refresh token endpoint.
     */
    refreshingPromise = null;
    constructor(config) {
        this.config = config;
        this.userManager = new UserManager(getAuthConfiguration(config));
        this.deviceCredentialsManager = new DeviceCredentialsManager();
        this.logoutMode = config.oidcConfiguration.logoutMode || 'redirect';
    }
    static mapOidcUserToDomainModel = (oidcUser) => {
        let passport;
        if (oidcUser.id_token) {
            passport = jwt_decode(oidcUser.id_token)?.passport;
        }
        const user = {
            expired: oidcUser.expired,
            idToken: oidcUser.id_token,
            accessToken: oidcUser.access_token,
            refreshToken: oidcUser.refresh_token,
            profile: {
                sub: oidcUser.profile.sub,
                email: oidcUser.profile.email,
                nickname: oidcUser.profile.nickname,
            },
        };
        if (passport?.imx_eth_address) {
            user.imx = {
                ethAddress: passport.imx_eth_address,
                starkAddress: passport.imx_stark_address,
                userAdminAddress: passport.imx_user_admin_address,
            };
        }
        if (passport?.zkevm_eth_address) {
            user.zkEvm = {
                ethAddress: passport?.zkevm_eth_address,
                userAdminAddress: passport?.zkevm_user_admin_address,
            };
        }
        return user;
    };
    static mapDeviceTokenResponseToOidcUser = (tokenResponse) => {
        const idTokenPayload = jwt_decode(tokenResponse.id_token);
        return new User({
            id_token: tokenResponse.id_token,
            access_token: tokenResponse.access_token,
            refresh_token: tokenResponse.refresh_token,
            token_type: tokenResponse.token_type,
            profile: {
                sub: idTokenPayload.sub,
                iss: idTokenPayload.iss,
                aud: idTokenPayload.aud,
                exp: idTokenPayload.exp,
                iat: idTokenPayload.iat,
                email: idTokenPayload.email,
                nickname: idTokenPayload.nickname,
                passport: idTokenPayload.passport,
            },
        });
    };
    /**
     * login
     * @param anonymousId Caller can pass an anonymousId if they want to associate their user's identity with immutable's internal instrumentation.
     */
    async login(anonymousId) {
        return withPassportError(async () => {
            const popupWindowTarget = 'passportLoginPrompt';
            const signinPopup = async () => (this.userManager.signinPopup({
                extraQueryParams: {
                    ...(this.userManager.settings?.extraQueryParams ?? {}),
                    rid: getDetail(Detail.RUNTIME_ID) || '',
                    third_party_a_id: anonymousId || '',
                },
                popupWindowFeatures: {
                    width: 410,
                    height: 450,
                },
                popupWindowTarget,
            }));
            // This promise attempts to open the signin popup, and displays the blocked popup overlay if necessary.
            return new Promise((resolve, reject) => {
                signinPopup()
                    .then((oidcUser) => {
                    resolve(AuthManager.mapOidcUserToDomainModel(oidcUser));
                })
                    .catch((error) => {
                    // Reject with the error if it is not caused by a blocked popup
                    if (!(error instanceof Error) || error.message !== 'Attempted to navigate on a disposed window') {
                        reject(error);
                        return;
                    }
                    // Popup was blocked; append the blocked popup overlay to allow the user to try again.
                    let popupHasBeenOpened = false;
                    const overlay = new Overlay(this.config.popupOverlayOptions, true);
                    overlay.append(async () => {
                        try {
                            if (!popupHasBeenOpened) {
                                // The user is attempting to open the popup again. It's safe to assume that this will not fail,
                                // as there are no async operations between the button interaction & the popup being opened.
                                popupHasBeenOpened = true;
                                const oidcUser = await signinPopup();
                                overlay.remove();
                                resolve(AuthManager.mapOidcUserToDomainModel(oidcUser));
                            }
                            else {
                                // The popup has already been opened. By calling `window.open` with the same target as the
                                // previously opened popup, no new window will be opened. Instead, the existing popup
                                // will be focused. This works as expected in most browsers at the time of implementation, but
                                // the following exceptions do exist:
                                // - Safari: Only the initial call will focus the window, subsequent calls will do nothing.
                                // - Firefox: The window will not be focussed, nothing will happen.
                                window.open('', popupWindowTarget);
                            }
                        }
                        catch (retryError) {
                            overlay.remove();
                            reject(retryError);
                        }
                    }, () => {
                        overlay.remove();
                        reject(new Error('Popup closed by user'));
                    });
                });
            });
        }, PassportErrorType.AUTHENTICATION_ERROR);
    }
    async getUserOrLogin() {
        let user = null;
        try {
            user = await this.getUser();
        }
        catch (err) {
            logger.warn('Failed to retrieve a cached user session', err);
        }
        return user || this.login();
    }
    async loginCallback() {
        return withPassportError(async () => { await this.userManager.signinCallback(); }, PassportErrorType.AUTHENTICATION_ERROR);
    }
    /**
     * loginWithDeviceFlow
     * @param anonymousId Caller can pass an anonymousId if they want to associate their user's identity with immutable's internal instrumentation.
     */
    async loginWithDeviceFlow(anonymousId) {
        return withPassportError(async () => {
            const response = await globalAxios.post(`${this.config.authenticationDomain}/oauth/device/code`, {
                client_id: this.config.oidcConfiguration.clientId,
                scope: this.config.oidcConfiguration.scope,
                audience: this.config.oidcConfiguration.audience,
            }, formUrlEncodedHeader);
            const rid = getDetail(Detail.RUNTIME_ID);
            return {
                code: response.data.user_code,
                deviceCode: response.data.device_code,
                url: `${response.data.verification_uri_complete}${rid ? `&rid=${rid}` : ''}${anonymousId ? `&third_party_a_id=${anonymousId}` : ''}`,
                interval: response.data.interval,
            };
        }, PassportErrorType.AUTHENTICATION_ERROR);
    }
    /* eslint-disable no-await-in-loop */
    async loginWithDeviceFlowCallback(deviceCode, interval, timeoutMs) {
        return withPassportError(async () => {
            const startTime = Date.now();
            const loopCondition = true;
            while (loopCondition) {
                if (timeoutMs != null && Date.now() - startTime > timeoutMs) {
                    throw new Error('Timed out');
                }
                await wait$2(interval * 1000);
                try {
                    const tokenResponse = await this.getDeviceFlowToken(deviceCode);
                    const oidcUser = AuthManager.mapDeviceTokenResponseToOidcUser(tokenResponse);
                    const user = AuthManager.mapOidcUserToDomainModel(oidcUser);
                    await this.userManager.storeUser(oidcUser);
                    return user;
                }
                catch (error) {
                    if (globalAxios.isAxiosError(error)) {
                        const responseError = error.response?.data;
                        switch (responseError.error) {
                            case 'authorization_pending':
                                break;
                            case 'slow_down':
                                break;
                            case 'expired_token':
                                throw new Error('Token expired, please log in again');
                            case 'access_denied':
                                throw new Error('User denied access');
                            default:
                                throw new Error('Error getting token');
                        }
                    }
                    else {
                        throw error;
                    }
                }
            }
            throw new Error('Failed to get credentials');
        }, PassportErrorType.AUTHENTICATION_ERROR);
    }
    /* eslint-enable no-await-in-loop */
    async getDeviceFlowToken(deviceCode) {
        const response = await globalAxios.post(`${this.config.authenticationDomain}/oauth/token`, {
            client_id: this.config.oidcConfiguration.clientId,
            grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
            device_code: deviceCode,
        }, formUrlEncodedHeader);
        return response.data;
    }
    getPKCEAuthorizationUrl() {
        const verifier = base64URLEncode(crypto.randomBytes(32));
        const challenge = base64URLEncode(sha256(verifier));
        // https://auth0.com/docs/secure/attack-protection/state-parameters
        const state = base64URLEncode(crypto.randomBytes(32));
        const { redirectUri, scope, audience, clientId, } = this.config.oidcConfiguration;
        this.deviceCredentialsManager.savePKCEData({ state, verifier });
        const pKCEAuthorizationUrl = new URL(authorizeEndpoint, this.config.authenticationDomain);
        pKCEAuthorizationUrl.searchParams.set('response_type', 'code');
        pKCEAuthorizationUrl.searchParams.set('code_challenge', challenge);
        pKCEAuthorizationUrl.searchParams.set('code_challenge_method', 'S256');
        pKCEAuthorizationUrl.searchParams.set('client_id', clientId);
        pKCEAuthorizationUrl.searchParams.set('redirect_uri', redirectUri);
        pKCEAuthorizationUrl.searchParams.set('state', state);
        if (scope)
            pKCEAuthorizationUrl.searchParams.set('scope', scope);
        if (audience)
            pKCEAuthorizationUrl.searchParams.set('audience', audience);
        return pKCEAuthorizationUrl.toString();
    }
    async loginWithPKCEFlowCallback(authorizationCode, state) {
        return withPassportError(async () => {
            const pkceData = this.deviceCredentialsManager.getPKCEData();
            if (!pkceData) {
                throw new Error('No code verifier or state for PKCE');
            }
            if (state !== pkceData.state) {
                throw new Error('Provided state does not match stored state');
            }
            const tokenResponse = await this.getPKCEToken(authorizationCode, pkceData.verifier);
            const oidcUser = AuthManager.mapDeviceTokenResponseToOidcUser(tokenResponse);
            const user = AuthManager.mapOidcUserToDomainModel(oidcUser);
            await this.userManager.storeUser(oidcUser);
            return user;
        }, PassportErrorType.AUTHENTICATION_ERROR);
    }
    async getPKCEToken(authorizationCode, codeVerifier) {
        const response = await globalAxios.post(`${this.config.authenticationDomain}/oauth/token`, {
            client_id: this.config.oidcConfiguration.clientId,
            grant_type: 'authorization_code',
            code_verifier: codeVerifier,
            code: authorizationCode,
            redirect_uri: this.config.oidcConfiguration.redirectUri,
        }, formUrlEncodedHeader);
        return response.data;
    }
    async logout() {
        return withPassportError(async () => {
            if (this.logoutMode === 'silent') {
                return this.userManager.signoutSilent();
            }
            return this.userManager.signoutRedirect();
        }, PassportErrorType.LOGOUT_ERROR);
    }
    async logoutSilentCallback(url) {
        return this.userManager.signoutSilentCallback(url);
    }
    async removeUser() {
        return this.userManager.removeUser();
    }
    async getDeviceFlowEndSessionEndpoint() {
        const { authenticationDomain, oidcConfiguration } = this.config;
        const endSessionEndpoint = new URL(logoutEndpoint, authenticationDomain);
        endSessionEndpoint.searchParams.set('client_id', oidcConfiguration.clientId);
        if (oidcConfiguration.logoutRedirectUri)
            endSessionEndpoint.searchParams.set('returnTo', oidcConfiguration.logoutRedirectUri);
        return endSessionEndpoint.toString();
    }
    forceUserRefreshInBackground() {
        this.refreshTokenAndUpdatePromise().catch((error) => {
            logger.warn('Failed to refresh user token', error);
        });
    }
    async forceUserRefresh() {
        return this.refreshTokenAndUpdatePromise();
    }
    /**
     * Refreshes the token and returns the user.
     * If the token is already being refreshed, returns the existing promise.
     */
    async refreshTokenAndUpdatePromise() {
        if (this.refreshingPromise)
            return this.refreshingPromise;
        // eslint-disable-next-line no-async-promise-executor
        this.refreshingPromise = new Promise(async (resolve, reject) => {
            try {
                const newOidcUser = await this.userManager.signinSilent();
                if (newOidcUser) {
                    resolve(AuthManager.mapOidcUserToDomainModel(newOidcUser));
                    return;
                }
                resolve(null);
            }
            catch (err) {
                let passportErrorType = PassportErrorType.AUTHENTICATION_ERROR;
                let errorMessage = 'Failed to refresh token';
                if (err instanceof ErrorTimeout) {
                    passportErrorType = PassportErrorType.SILENT_LOGIN_ERROR;
                }
                else if (err instanceof ErrorResponse) {
                    passportErrorType = PassportErrorType.NOT_LOGGED_IN_ERROR;
                    errorMessage = `${err.message}: ${err.error_description}`;
                }
                else if (err instanceof Error) {
                    errorMessage = err.message;
                }
                reject(new PassportError(errorMessage, passportErrorType));
            }
            finally {
                this.refreshingPromise = null; // Reset the promise after completion
            }
        });
        return this.refreshingPromise;
    }
    /**
     *
     * @param typeAssertion {(user: User) => boolean} - Optional. If provided, then the User will be checked against
     * the typeAssertion. If the user meets the requirements, then it will be typed as T and returned. If the User
     * does NOT meet the type assertion, then execution will continue, and we will attempt to obtain a User that does
     * meet the type assertion.
     *
     * This function will attempt to obtain a User in the following order:
     * 1. If the User is currently refreshing, wait for the refresh to complete.
     * 2. Attempt to obtain a User from storage that has not expired.
     * 3. Attempt to refresh the User if a refresh token is present.
     * 4. Return null if no valid User can be obtained.
     */
    async getUser(typeAssertion = (user) => true) {
        if (this.refreshingPromise) {
            const user = await this.refreshingPromise;
            if (user && typeAssertion(user)) {
                return user;
            }
            return null;
        }
        const oidcUser = await this.userManager.getUser();
        if (!oidcUser)
            return null;
        if (!isTokenExpired(oidcUser)) {
            const user = AuthManager.mapOidcUserToDomainModel(oidcUser);
            if (user && typeAssertion(user)) {
                return user;
            }
        }
        if (oidcUser.refresh_token) {
            const user = await this.refreshTokenAndUpdatePromise();
            if (user && typeAssertion(user)) {
                return user;
            }
        }
        return null;
    }
    async getUserZkEvm() {
        const user = await this.getUser(isUserZkEvm);
        if (!user) {
            throw new Error('Failed to obtain a User with the required ZkEvm attributes');
        }
        return user;
    }
    async getUserImx() {
        const user = await this.getUser(isUserImx);
        if (!user) {
            throw new Error('Failed to obtain a User with the required IMX attributes');
        }
        return user;
    }
}

const lazyLoad = (promiseToAwait, initialiseFunction) => promiseToAwait().then(initialiseFunction);
const lazyDocumentReady = (initialiseFunction) => {
    const documentReadyPromise = () => new Promise((resolve) => {
        const onReadyStateChange = () => {
            if (window.document.readyState === 'complete') {
                resolve();
                window.document.removeEventListener('readystatechange', onReadyStateChange);
            }
        };
        // Add a handler before checking `readyState` to ensure that we don't miss the event
        window.document.addEventListener('readystatechange', onReadyStateChange);
        if (window.document.readyState === 'complete') {
            resolve();
            window.document.removeEventListener('readystatechange', onReadyStateChange);
        }
    });
    return lazyLoad(documentReadyPromise, initialiseFunction);
};

const withMetrics = (fn, flowName) => {
    const flow = trackFlow('passport', flowName);
    try {
        return fn(flow);
    }
    catch (error) {
        if (error instanceof Error) {
            trackError('passport', flowName, error);
        }
        flow.addEvent('errored');
        throw error;
    }
    finally {
        flow.addEvent('End');
    }
};
const withMetricsAsync = async (fn, flowName) => {
    const flow = trackFlow('passport', flowName);
    try {
        return await fn(flow);
    }
    catch (error) {
        if (error instanceof Error) {
            trackError('passport', flowName, error);
        }
        flow.addEvent('errored');
        throw error;
    }
    finally {
        flow.addEvent('End');
    }
};

const MAINNET = 'mainnet';
class MagicAdapter {
    config;
    lazyMagicClient;
    constructor(config) {
        this.config = config;
        if (typeof window !== 'undefined') {
            this.lazyMagicClient = lazyDocumentReady(() => {
                const client = new Magic(this.config.magicPublishableApiKey, {
                    extensions: [new OpenIdExtension()],
                    network: MAINNET, // We always connect to mainnet to ensure addresses are the same across envs
                });
                return client;
            });
        }
    }
    get magicClient() {
        if (!this.lazyMagicClient) {
            throw new Error('Cannot perform this action outside of the browser');
        }
        return this.lazyMagicClient;
    }
    async login(idToken) {
        return withPassportError(async () => (withMetricsAsync(async (flow) => {
            const startTime = performance.now();
            const magicClient = await this.magicClient;
            flow.addEvent('endMagicClientInit');
            await magicClient.openid.loginWithOIDC({
                jwt: idToken,
                providerId: this.config.magicProviderId,
            });
            flow.addEvent('endLoginWithOIDC');
            trackDuration('passport', flow.details.flowName, Math.round(performance.now() - startTime));
            return magicClient.rpcProvider;
        }, 'magicLogin')), PassportErrorType.WALLET_CONNECTION_ERROR);
    }
    async logout() {
        const magicClient = await this.magicClient;
        if (magicClient.user) {
            await magicClient.user.logout();
        }
    }
}

const getStarkSigner = async (signer) => withPassportError(async () => {
    const privateKey = await generateLegacyStarkPrivateKey(signer);
    return createStarkSigner(privateKey);
}, PassportErrorType.WALLET_CONNECTION_ERROR);

async function exchangeTransfer({ user, starkSigner, request, exchangesApi, }) {
    return withPassportError(async () => {
        const { ethAddress } = user.imx;
        const transferAmount = request.amount;
        const signableResult = await exchangesApi.getExchangeSignableTransfer({
            id: request.transactionID,
            getSignableTransferRequest: {
                sender: ethAddress,
                token: convertToSignableToken(request),
                amount: transferAmount,
                receiver: request.receiver,
            },
        });
        const starkAddress = await starkSigner.getAddress();
        const { payload_hash: payloadHash } = signableResult.data;
        const starkSignature = await starkSigner.signMessage(payloadHash);
        const transferSigningParams = {
            sender_stark_key: signableResult.data.sender_stark_key || starkAddress,
            sender_vault_id: signableResult.data.sender_vault_id,
            receiver_stark_key: signableResult.data.receiver_stark_key,
            receiver_vault_id: signableResult.data.receiver_vault_id,
            asset_id: signableResult.data.asset_id,
            amount: signableResult.data.amount,
            nonce: signableResult.data.nonce,
            expiration_timestamp: signableResult.data.expiration_timestamp,
            stark_signature: starkSignature,
        };
        const headers = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Authorization: `Bearer ${user.accessToken}`,
        };
        const response = await exchangesApi.createExchangeTransfer({
            id: request.transactionID,
            createTransferRequest: transferSigningParams,
        }, { headers });
        return {
            sent_signature: response?.data.sent_signature,
            status: response?.data.status?.toString(),
            time: response?.data.time,
            transfer_id: response?.data.transfer_id,
        };
    }, PassportErrorType.EXCHANGE_TRANSFER_ERROR);
}

const ERC721$1 = 'ERC721';
async function createOrder({ starkSigner, user, request, ordersApi, guardianClient, }) {
    return withPassportError(async () => {
        const { ethAddress } = user.imx;
        const amountSell = request.sell.type === ERC721$1 ? '1' : request.sell.amount;
        const amountBuy = request.buy.type === ERC721$1 ? '1' : request.buy.amount;
        const headers = { Authorization: `Bearer ${user.accessToken}` };
        const getSignableOrderRequestV3 = {
            user: ethAddress,
            amount_buy: amountBuy,
            token_buy: convertToSignableToken(request.buy),
            amount_sell: amountSell,
            token_sell: convertToSignableToken(request.sell),
            fees: request.fees,
            split_fees: true,
            expiration_timestamp: request.expiration_timestamp,
        };
        const getSignableOrderResponse = await ordersApi.getSignableOrder({
            getSignableOrderRequestV3,
        }, { headers });
        await guardianClient.evaluateImxTransaction({
            payloadHash: getSignableOrderResponse.data.payload_hash,
        });
        const { payload_hash: payloadHash } = getSignableOrderResponse.data;
        const starkSignature = await starkSigner.signMessage(payloadHash);
        const signableResultData = getSignableOrderResponse.data;
        const orderParams = {
            createOrderRequest: {
                include_fees: true,
                fees: request.fees,
                stark_signature: starkSignature,
                amount_buy: signableResultData.amount_buy,
                amount_sell: signableResultData.amount_sell,
                asset_id_buy: signableResultData.asset_id_buy,
                asset_id_sell: signableResultData.asset_id_sell,
                expiration_timestamp: signableResultData.expiration_timestamp,
                nonce: signableResultData.nonce,
                stark_key: signableResultData.stark_key,
                vault_id_buy: signableResultData.vault_id_buy,
                vault_id_sell: signableResultData.vault_id_sell,
            },
        };
        const createOrderResponse = await ordersApi.createOrderV3(orderParams, {
            headers,
        });
        return {
            ...createOrderResponse.data,
        };
    }, PassportErrorType.CREATE_ORDER_ERROR);
}
async function cancelOrder({ user, starkSigner, request, ordersApi, guardianClient, }) {
    return withPassportError(async () => {
        const getSignableCancelOrderRequest = {
            order_id: request.order_id,
        };
        const headers = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Authorization: `Bearer ${user.accessToken}`,
        };
        const getSignableCancelOrderResponse = await ordersApi.getSignableCancelOrderV3({
            getSignableCancelOrderRequest,
        }, { headers });
        await guardianClient.evaluateImxTransaction({
            payloadHash: getSignableCancelOrderResponse.data.payload_hash,
        });
        const { payload_hash: payloadHash } = getSignableCancelOrderResponse.data;
        const starkSignature = await starkSigner.signMessage(payloadHash);
        const cancelOrderResponse = await ordersApi.cancelOrderV3({
            id: request.order_id.toString(),
            cancelOrderRequest: {
                order_id: request.order_id,
                stark_signature: starkSignature,
            },
        }, { headers });
        return {
            order_id: cancelOrderResponse.data.order_id,
            status: cancelOrderResponse.data.status,
        };
    }, PassportErrorType.CANCEL_ORDER_ERROR);
}

async function registerPassport({ ethSigner, starkSigner, imxApiClients }, authorization) {
    return withPassportError(async () => {
        const [userAddress, starkPublicKey] = await Promise.all([
            ethSigner.getAddress(),
            starkSigner.getAddress(),
        ]);
        const signableResult = await imxApiClients.usersApi.getSignableRegistrationOffchain({
            getSignableRegistrationRequest: {
                ether_key: userAddress,
                stark_key: starkPublicKey,
            },
        });
        const { signable_message: signableMessage, payload_hash: payloadHash } = signableResult.data;
        const [ethSignature, starkSignature] = await Promise.all([
            signRaw(signableMessage, ethSigner),
            starkSigner.signMessage(payloadHash),
        ]);
        const response = await imxApiClients.usersApi.registerPassportUserV2({
            authorization: `Bearer ${authorization}`,
            registerPassportUserRequest: {
                eth_signature: ethSignature,
                ether_key: userAddress,
                stark_signature: starkSignature,
                stark_key: starkPublicKey,
            },
        });
        return response.data;
    }, PassportErrorType.USER_REGISTRATION_ERROR);
}

async function createTrade({ request, tradesApi, user, starkSigner, guardianClient, }) {
    return withPassportError(async () => {
        const { ethAddress } = user.imx;
        const getSignableTradeRequest = {
            expiration_timestamp: request.expiration_timestamp,
            fees: request.fees,
            order_id: request.order_id,
            user: ethAddress,
        };
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const headers = { Authorization: `Bearer ${user.accessToken}` };
        const getSignableTradeResponse = await tradesApi.getSignableTrade({
            getSignableTradeRequest,
        }, { headers });
        await guardianClient.evaluateImxTransaction({
            payloadHash: getSignableTradeResponse.data.payload_hash,
        });
        const { payload_hash: payloadHash } = getSignableTradeResponse.data;
        const starkSignature = await starkSigner.signMessage(payloadHash);
        const { data: signableResultData } = getSignableTradeResponse;
        const tradeParams = {
            createTradeRequest: {
                include_fees: true,
                fees: request?.fees,
                stark_signature: starkSignature,
                order_id: request?.order_id,
                fee_info: signableResultData.fee_info,
                amount_buy: signableResultData.amount_buy,
                amount_sell: signableResultData.amount_sell,
                asset_id_buy: signableResultData.asset_id_buy,
                asset_id_sell: signableResultData.asset_id_sell,
                expiration_timestamp: signableResultData.expiration_timestamp,
                nonce: signableResultData.nonce,
                stark_key: signableResultData.stark_key,
                vault_id_buy: signableResultData.vault_id_buy,
                vault_id_sell: signableResultData.vault_id_sell,
            },
        };
        const { data: createTradeResponse } = await tradesApi.createTradeV3(tradeParams, {
            headers,
        });
        return createTradeResponse;
    }, PassportErrorType.CREATE_TRADE_ERROR);
}

const ERC721 = 'ERC721';
async function transfer({ request, transfersApi, starkSigner, user, guardianClient, }) {
    return withPassportError(async () => {
        const transferAmount = request.type === ERC721 ? '1' : request.amount;
        const getSignableTransferRequest = {
            sender: user.imx.ethAddress,
            token: convertToSignableToken(request),
            amount: transferAmount,
            receiver: request.receiver,
        };
        const headers = {
            Authorization: `Bearer ${user.accessToken}`,
        };
        const signableResult = await transfersApi.getSignableTransferV1({
            getSignableTransferRequest,
        }, { headers });
        await guardianClient.evaluateImxTransaction({
            payloadHash: signableResult.data.payload_hash,
        });
        const signableResultData = signableResult.data;
        const { payload_hash: payloadHash } = signableResultData;
        const starkSignature = await starkSigner.signMessage(payloadHash);
        const senderStarkKey = await starkSigner.getAddress();
        const transferSigningParams = {
            sender_stark_key: signableResultData.sender_stark_key || senderStarkKey,
            sender_vault_id: signableResultData.sender_vault_id,
            receiver_stark_key: signableResultData.receiver_stark_key,
            receiver_vault_id: signableResultData.receiver_vault_id,
            asset_id: signableResultData.asset_id,
            amount: signableResultData.amount,
            nonce: signableResultData.nonce,
            expiration_timestamp: signableResultData.expiration_timestamp,
            stark_signature: starkSignature,
        };
        const createTransferRequest = {
            createTransferRequest: transferSigningParams,
        };
        const { data: responseData } = await transfersApi.createTransferV1(createTransferRequest, { headers });
        return {
            sent_signature: responseData.sent_signature,
            status: responseData.status?.toString(),
            time: responseData.time,
            transfer_id: responseData.transfer_id,
        };
    }, PassportErrorType.TRANSFER_ERROR);
}
async function batchNftTransfer({ user, starkSigner, request, transfersApi, guardianClient, }) {
    return withPassportError(async () => {
        const { ethAddress } = user.imx;
        const signableRequests = request.map((nftTransfer) => ({
            amount: '1',
            token: convertToSignableToken({
                type: ERC721,
                tokenId: nftTransfer.tokenId,
                tokenAddress: nftTransfer.tokenAddress,
            }),
            receiver: nftTransfer.receiver,
        }));
        const headers = { Authorization: `Bearer ${user.accessToken}` };
        const signableResult = await transfersApi.getSignableTransfer({
            getSignableTransferRequestV2: {
                sender_ether_key: ethAddress,
                signable_requests: signableRequests,
            },
        }, { headers });
        await guardianClient.evaluateImxTransaction({
            payloadHash: signableResult.data.signable_responses[0]?.payload_hash,
        });
        const requests = await Promise.all(signableResult.data.signable_responses.map(async (resp) => {
            const starkSignature = await starkSigner.signMessage(resp.payload_hash);
            return {
                sender_vault_id: resp.sender_vault_id,
                receiver_stark_key: resp.receiver_stark_key,
                receiver_vault_id: resp.receiver_vault_id,
                asset_id: resp.asset_id,
                amount: resp.amount,
                nonce: resp.nonce,
                expiration_timestamp: resp.expiration_timestamp,
                stark_signature: starkSignature,
            };
        }));
        const transferSigningParams = {
            sender_stark_key: signableResult.data.sender_stark_key,
            requests,
        };
        const response = await transfersApi.createTransfer({
            createTransferRequestV2: transferSigningParams,
        }, { headers });
        return {
            transfer_ids: response?.data.transfer_ids,
        };
    }, PassportErrorType.TRANSFER_ERROR);
}

const POLL_INTERVAL = 1 * 1000; // every 1 second
const MAX_RETRIES = 3;
const wait$1 = (ms) => new Promise((resolve) => {
    setTimeout(() => resolve(), ms);
});
const retryWithDelay = async (fn, options) => {
    const { retries = MAX_RETRIES, interval = POLL_INTERVAL, finalErr = Error('Retry failed'), finallyFn = () => { }, } = options || {};
    try {
        return await fn();
    }
    catch (err) {
        if (retries <= 0) {
            return Promise.reject(finalErr);
        }
        await wait$1(interval);
        return retryWithDelay(fn, { retries: retries - 1, finalErr, finallyFn });
    }
    finally {
        if (retries <= 0) {
            finallyFn();
        }
    }
};

async function forceUserRefresh(authManager) {
    // User metadata is updated asynchronously. Poll userinfo endpoint until it is updated.
    await retryWithDelay(async () => {
        const user = await authManager.forceUserRefresh(); // force refresh to get updated user info
        if (user?.imx)
            return user;
        return Promise.reject(new Error('user wallet addresses not exist'));
    });
}
async function registerOffchain(userAdminKeySigner, starkSigner, unregisteredUser, authManager, imxApiClients) {
    return withPassportError(async () => {
        try {
            const response = await registerPassport({
                ethSigner: userAdminKeySigner,
                starkSigner,
                imxApiClients,
            }, unregisteredUser.accessToken);
            await forceUserRefresh(authManager);
            return response;
        }
        catch (err) {
            if (globalAxios.isAxiosError(err) && err.response?.status === 409) {
                // The user already registered, but the user token is not updated yet.
                await forceUserRefresh(authManager);
                return { tx_hash: '' };
            }
            throw err;
        }
    }, PassportErrorType.USER_REGISTRATION_ERROR);
}

class PassportImxProvider {
    authManager;
    immutableXClient;
    guardianClient;
    imxApiClients;
    magicAdapter;
    /**
     * This property is set during initialisation and stores the signers in a promise.
     * This property is not meant to be accessed directly, but through the
     * `#getSigners` method.
     * @see #getSigners
     */
    signers;
    signerInitialisationError;
    constructor({ authManager, immutableXClient, passportEventEmitter, magicAdapter, imxApiClients, guardianClient, }) {
        this.authManager = authManager;
        this.immutableXClient = immutableXClient;
        this.magicAdapter = magicAdapter;
        this.imxApiClients = imxApiClients;
        this.guardianClient = guardianClient;
        this.#initialiseSigners();
        passportEventEmitter.on(PassportEvents.LOGGED_OUT, this.handleLogout);
    }
    handleLogout = () => {
        this.signers = undefined;
    };
    /**
     * This method is called by the constructor and asynchronously initialises the signers.
     * The signers are stored in a promise so that they can be retrieved by the provider
     * when needed.
     *
     * If an error is thrown during initialisation, it is stored in the `signerInitialisationError`,
     * so that it doesn't result in an unhandled promise rejection.
     *
     * This error is thrown when the signers are requested through:
     * @see #getSigners
     *
     */
    #initialiseSigners() {
        const generateSigners = async () => {
            const user = await this.authManager.getUser();
            // The user will be present because the factory validates it
            const magicRpcProvider = await this.magicAdapter.login(user.idToken);
            const web3Provider = new Web3Provider(magicRpcProvider);
            const ethSigner = web3Provider.getSigner();
            const starkSigner = await getStarkSigner(ethSigner);
            return { ethSigner, starkSigner };
        };
        // eslint-disable-next-line no-async-promise-executor
        this.signers = new Promise(async (resolve) => {
            try {
                resolve(await generateSigners());
            }
            catch (err) {
                // Capture and store the initialization error
                this.signerInitialisationError = err;
                resolve(undefined);
            }
        });
    }
    async #getAuthenticatedUser() {
        const user = await this.authManager.getUser();
        if (!user || !this.signers) {
            throw new PassportError('User has been logged out', PassportErrorType.NOT_LOGGED_IN_ERROR);
        }
        return user;
    }
    async #getSigners() {
        const signers = await this.signers;
        // Throw the stored error if the signers failed to initialise
        if (typeof signers === 'undefined') {
            if (typeof this.signerInitialisationError !== 'undefined') {
                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                throw this.signerInitialisationError;
            }
            throw new Error('Signers failed to initialise');
        }
        return signers;
    }
    async #getRegisteredImxUserAndSigners() {
        const [user, signers] = await Promise.all([
            this.#getAuthenticatedUser(),
            this.#getSigners(),
        ]);
        if (!isUserImx(user)) {
            throw new PassportError('User has not been registered with StarkEx', PassportErrorType.USER_NOT_REGISTERED_ERROR);
        }
        return {
            user,
            starkSigner: signers.starkSigner,
            ethSigner: signers.ethSigner,
        };
    }
    async transfer(request) {
        return withMetricsAsync(() => this.guardianClient.withDefaultConfirmationScreenTask(async () => {
            const { user, starkSigner } = await this.#getRegisteredImxUserAndSigners();
            return transfer({
                request,
                user,
                starkSigner,
                transfersApi: this.immutableXClient.transfersApi,
                guardianClient: this.guardianClient,
            });
        })(), 'imxTransfer');
    }
    async registerOffchain() {
        return withMetricsAsync(async () => {
            const [user, signers] = await Promise.all([
                this.#getAuthenticatedUser(),
                this.#getSigners(),
            ]);
            return await registerOffchain(signers.ethSigner, signers.starkSigner, user, this.authManager, this.imxApiClients);
        }, 'imxRegisterOffchain');
    }
    async isRegisteredOffchain() {
        return withMetricsAsync(async () => {
            const user = await this.#getAuthenticatedUser();
            return !!user.imx;
        }, 'imxIsRegisteredOffchain');
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    isRegisteredOnchain() {
        throw new PassportError('Operation not supported', PassportErrorType.OPERATION_NOT_SUPPORTED_ERROR);
    }
    async createOrder(request) {
        return withMetricsAsync(() => this.guardianClient.withDefaultConfirmationScreenTask(async () => {
            const { user, starkSigner } = await this.#getRegisteredImxUserAndSigners();
            return createOrder({
                request,
                user,
                starkSigner,
                ordersApi: this.immutableXClient.ordersApi,
                guardianClient: this.guardianClient,
            });
        })(), 'imxCreateOrder');
    }
    async cancelOrder(request) {
        return withMetricsAsync(() => this.guardianClient.withDefaultConfirmationScreenTask(async () => {
            const { user, starkSigner } = await this.#getRegisteredImxUserAndSigners();
            return cancelOrder({
                request,
                user,
                starkSigner,
                ordersApi: this.immutableXClient.ordersApi,
                guardianClient: this.guardianClient,
            });
        })(), 'imxCancelOrder');
    }
    async createTrade(request) {
        return withMetricsAsync(() => this.guardianClient.withDefaultConfirmationScreenTask(async () => {
            const { user, starkSigner } = await this.#getRegisteredImxUserAndSigners();
            return createTrade({
                request,
                user,
                starkSigner,
                tradesApi: this.immutableXClient.tradesApi,
                guardianClient: this.guardianClient,
            });
        })(), 'imxCreateTrade');
    }
    async batchNftTransfer(request) {
        return withMetricsAsync(() => this.guardianClient.withConfirmationScreenTask({ width: 480, height: 784 })(async () => {
            const { user, starkSigner } = await this.#getRegisteredImxUserAndSigners();
            return batchNftTransfer({
                request,
                user,
                starkSigner,
                transfersApi: this.immutableXClient.transfersApi,
                guardianClient: this.guardianClient,
            });
        })(), 'imxBatchNftTransfer');
    }
    async exchangeTransfer(request) {
        return withMetricsAsync(async () => {
            const { user, starkSigner } = await this.#getRegisteredImxUserAndSigners();
            return exchangeTransfer({
                request,
                user,
                starkSigner,
                exchangesApi: this.immutableXClient.exchangeApi,
            });
        }, 'imxExchangeTransfer');
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    deposit(deposit) {
        throw new PassportError('Operation not supported', PassportErrorType.OPERATION_NOT_SUPPORTED_ERROR);
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    prepareWithdrawal(request) {
        throw new PassportError('Operation not supported', PassportErrorType.OPERATION_NOT_SUPPORTED_ERROR);
    }
    // eslint-disable-next-line class-methods-use-this
    completeWithdrawal(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    starkPublicKey, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    token) {
        throw new PassportError('Operation not supported', PassportErrorType.OPERATION_NOT_SUPPORTED_ERROR);
    }
    async getAddress() {
        return withMetricsAsync(async () => {
            const user = await this.#getAuthenticatedUser();
            if (!isUserImx(user)) {
                throw new PassportError('User has not been registered with StarkEx', PassportErrorType.USER_NOT_REGISTERED_ERROR);
            }
            return Promise.resolve(user.imx.ethAddress);
        }, 'imxGetAddress');
    }
}

class PassportImxProviderFactory {
    authManager;
    immutableXClient;
    magicAdapter;
    passportEventEmitter;
    imxApiClients;
    guardianClient;
    constructor({ authManager, immutableXClient, magicAdapter, passportEventEmitter, imxApiClients, guardianClient, }) {
        this.authManager = authManager;
        this.immutableXClient = immutableXClient;
        this.magicAdapter = magicAdapter;
        this.passportEventEmitter = passportEventEmitter;
        this.imxApiClients = imxApiClients;
        this.guardianClient = guardianClient;
    }
    async getProvider() {
        const user = await this.authManager.getUserOrLogin();
        return this.createProviderInstance(user);
    }
    async getProviderSilent() {
        const user = await this.authManager.getUser();
        if (!user) {
            return null;
        }
        return this.createProviderInstance(user);
    }
    async createProviderInstance(user) {
        if (!user.idToken) {
            throw new PassportError('Failed to initialise', PassportErrorType.WALLET_CONNECTION_ERROR);
        }
        return new PassportImxProvider({
            authManager: this.authManager,
            immutableXClient: this.immutableXClient,
            passportEventEmitter: this.passportEventEmitter,
            magicAdapter: this.magicAdapter,
            imxApiClients: this.imxApiClients,
            guardianClient: this.guardianClient,
        });
    }
}

const validateConfiguration = (configuration, requiredKeys, prefix) => {
    const missingKeys = requiredKeys
        .map((key) => !configuration[key] && key)
        .filter((n) => n)
        .join(', ');
    if (missingKeys !== '') {
        const errorMessage = prefix
            ? `${prefix} - ${missingKeys} cannot be null`
            : `${missingKeys} cannot be null`;
        throw new PassportError(errorMessage, PassportErrorType.INVALID_CONFIGURATION);
    }
};
class PassportConfiguration {
    authenticationDomain;
    passportDomain;
    imxPublicApiDomain;
    magicPublishableApiKey;
    magicProviderId;
    oidcConfiguration;
    baseConfig;
    zkEvmRpcUrl;
    relayerUrl;
    multiRollupConfig;
    crossSdkBridgeEnabled;
    jsonRpcReferrer;
    forceScwDeployBeforeMessageSignature;
    popupOverlayOptions;
    constructor({ baseConfig, overrides, crossSdkBridgeEnabled, jsonRpcReferrer, forceScwDeployBeforeMessageSignature, popupOverlayOptions, ...oidcConfiguration }) {
        validateConfiguration(oidcConfiguration, [
            'clientId',
            'redirectUri',
        ]);
        this.oidcConfiguration = oidcConfiguration;
        this.baseConfig = baseConfig;
        this.crossSdkBridgeEnabled = crossSdkBridgeEnabled || false;
        this.jsonRpcReferrer = jsonRpcReferrer || '';
        this.forceScwDeployBeforeMessageSignature = forceScwDeployBeforeMessageSignature || false;
        this.popupOverlayOptions = popupOverlayOptions || {
            disableGenericPopupOverlay: false,
            disableBlockedPopupOverlay: false,
        };
        if (overrides) {
            validateConfiguration(overrides, [
                'authenticationDomain',
                'passportDomain',
                'magicPublishableApiKey',
                'magicProviderId',
                'zkEvmRpcUrl',
                'relayerUrl',
                'imxPublicApiDomain',
                'indexerMrBasePath',
                'orderBookMrBasePath',
                'passportMrBasePath',
            ], 'overrides');
            this.authenticationDomain = overrides.authenticationDomain;
            this.passportDomain = overrides.passportDomain;
            this.imxPublicApiDomain = overrides.imxPublicApiDomain;
            this.magicPublishableApiKey = overrides.magicPublishableApiKey;
            this.magicProviderId = overrides.magicProviderId;
            this.zkEvmRpcUrl = overrides.zkEvmRpcUrl;
            this.relayerUrl = overrides.relayerUrl;
            this.multiRollupConfig = {
                indexer: createConfig({
                    basePath: overrides.indexerMrBasePath,
                }),
                orderBook: createConfig({
                    basePath: overrides.orderBookMrBasePath,
                }),
                passport: createConfig({
                    basePath: overrides.passportMrBasePath,
                }),
            };
        }
        else {
            switch (baseConfig.environment) {
                case Environment.PRODUCTION: {
                    this.authenticationDomain = 'https://auth.immutable.com';
                    this.magicPublishableApiKey = 'pk_live_10F423798A540ED7';
                    this.magicProviderId = 'fSMzaRQ4O7p4fttl7pCyGVtJS_G70P8SNsLXtPPGHo0=';
                    this.passportDomain = 'https://passport.immutable.com';
                    this.imxPublicApiDomain = 'https://api.immutable.com';
                    this.zkEvmRpcUrl = 'https://rpc.immutable.com';
                    this.relayerUrl = 'https://api.immutable.com/relayer-mr';
                    this.multiRollupConfig = multiRollupConfig.getProduction();
                    break;
                }
                case Environment.SANDBOX:
                default: {
                    this.authenticationDomain = 'https://auth.immutable.com';
                    this.magicPublishableApiKey = 'pk_live_10F423798A540ED7';
                    this.magicProviderId = 'fSMzaRQ4O7p4fttl7pCyGVtJS_G70P8SNsLXtPPGHo0=';
                    this.passportDomain = 'https://passport.sandbox.immutable.com';
                    this.imxPublicApiDomain = 'https://api.sandbox.immutable.com';
                    this.zkEvmRpcUrl = 'https://rpc.testnet.immutable.com';
                    this.relayerUrl = 'https://api.sandbox.immutable.com/relayer-mr';
                    this.multiRollupConfig = multiRollupConfig.getSandbox();
                    break;
                }
            }
        }
    }
}

var SendMessage;
(function (SendMessage) {
    SendMessage["CONFIRMATION_START"] = "confirmation_start";
})(SendMessage || (SendMessage = {}));
var ReceiveMessage;
(function (ReceiveMessage) {
    ReceiveMessage["CONFIRMATION_WINDOW_READY"] = "confirmation_window_ready";
    ReceiveMessage["TRANSACTION_CONFIRMED"] = "transaction_confirmed";
    ReceiveMessage["TRANSACTION_ERROR"] = "transaction_error";
    ReceiveMessage["TRANSACTION_REJECTED"] = "transaction_rejected";
    ReceiveMessage["MESSAGE_CONFIRMED"] = "message_confirmed";
    ReceiveMessage["MESSAGE_ERROR"] = "message_error";
    ReceiveMessage["MESSAGE_REJECTED"] = "message_rejected";
})(ReceiveMessage || (ReceiveMessage = {}));
const PASSPORT_EVENT_TYPE = 'imx_passport_confirmation';

const openPopupCenter = ({ url, title, width, height, }) => {
    const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
    const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
    const newWindow = window.open(url, title, `
      scrollbars=yes,
      width=${width}, 
      height=${height}, 
      top=${top}, 
      left=${left}
     `);
    if (!newWindow) {
        throw new Error('Failed to open confirmation screen');
    }
    newWindow.focus();
    return newWindow;
};

const CONFIRMATION_WINDOW_TITLE = 'Confirm this transaction';
const CONFIRMATION_WINDOW_HEIGHT = 720;
const CONFIRMATION_WINDOW_WIDTH = 480;
const CONFIRMATION_WINDOW_CLOSED_POLLING_DURATION = 1000;
class ConfirmationScreen {
    config;
    confirmationWindow;
    popupOptions;
    overlay;
    overlayClosed;
    timer;
    constructor(config) {
        this.config = config;
        this.overlayClosed = false;
    }
    getHref(relativePath, queryStringParams) {
        let href = `${this.config.passportDomain}/transaction-confirmation/${relativePath}`;
        if (queryStringParams) {
            const queryString = queryStringParams
                ? Object.keys(queryStringParams)
                    .map((key) => `${key}=${queryStringParams[key]}`)
                    .join('&')
                : '';
            href = `${href}?${queryString}`;
        }
        return href;
    }
    requestConfirmation(transactionId, etherAddress, chainType, chainId) {
        return new Promise((resolve, reject) => {
            const messageHandler = ({ data, origin }) => {
                if (origin !== this.config.passportDomain
                    || data.eventType !== PASSPORT_EVENT_TYPE) {
                    return;
                }
                switch (data.messageType) {
                    case ReceiveMessage.CONFIRMATION_WINDOW_READY: {
                        this.confirmationWindow?.postMessage({
                            eventType: PASSPORT_EVENT_TYPE,
                            messageType: SendMessage.CONFIRMATION_START,
                        }, this.config.passportDomain);
                        break;
                    }
                    case ReceiveMessage.TRANSACTION_CONFIRMED: {
                        this.closeWindow();
                        resolve({ confirmed: true });
                        break;
                    }
                    case ReceiveMessage.TRANSACTION_REJECTED: {
                        this.closeWindow();
                        resolve({ confirmed: false });
                        break;
                    }
                    case ReceiveMessage.TRANSACTION_ERROR: {
                        this.closeWindow();
                        reject(new Error('Error during transaction confirmation'));
                        break;
                    }
                    default:
                        this.closeWindow();
                        reject(new Error('Unsupported message type'));
                }
            };
            let href = '';
            if (chainType === index$1.TransactionApprovalRequestChainTypeEnum.Starkex) {
                href = this.getHref('transaction', { transactionId, etherAddress, chainType });
            }
            else {
                href = this.getHref('zkevm/transaction', {
                    transactionID: transactionId, etherAddress, chainType, chainID: chainId,
                });
            }
            window.addEventListener('message', messageHandler);
            this.showConfirmationScreen(href, messageHandler, resolve);
        });
    }
    requestMessageConfirmation(messageID, etherAddress, messageType) {
        return new Promise((resolve, reject) => {
            const messageHandler = ({ data, origin }) => {
                if (origin !== this.config.passportDomain
                    || data.eventType !== PASSPORT_EVENT_TYPE) {
                    return;
                }
                switch (data.messageType) {
                    case ReceiveMessage.CONFIRMATION_WINDOW_READY: {
                        this.confirmationWindow?.postMessage({
                            eventType: PASSPORT_EVENT_TYPE,
                            messageType: SendMessage.CONFIRMATION_START,
                        }, this.config.passportDomain);
                        break;
                    }
                    case ReceiveMessage.MESSAGE_CONFIRMED: {
                        this.closeWindow();
                        resolve({ confirmed: true });
                        break;
                    }
                    case ReceiveMessage.MESSAGE_REJECTED: {
                        this.closeWindow();
                        resolve({ confirmed: false });
                        break;
                    }
                    case ReceiveMessage.MESSAGE_ERROR: {
                        this.closeWindow();
                        reject(new Error('Error during message confirmation'));
                        break;
                    }
                    default:
                        this.closeWindow();
                        reject(new Error('Unsupported message type'));
                }
            };
            window.addEventListener('message', messageHandler);
            const href = this.getHref('zkevm/message', {
                messageID,
                etherAddress,
                ...(messageType ? { messageType } : {}),
            });
            this.showConfirmationScreen(href, messageHandler, resolve);
        });
    }
    loading(popupOptions) {
        if (this.config.crossSdkBridgeEnabled) {
            // There is no need to open a confirmation window if cross-sdk bridge is enabled
            return;
        }
        this.popupOptions = popupOptions;
        try {
            this.confirmationWindow = openPopupCenter({
                url: this.getHref('loading'),
                title: CONFIRMATION_WINDOW_TITLE,
                width: popupOptions?.width || CONFIRMATION_WINDOW_WIDTH,
                height: popupOptions?.height || CONFIRMATION_WINDOW_HEIGHT,
            });
            this.overlay = new Overlay(this.config.popupOverlayOptions);
        }
        catch (error) {
            // If an error is thrown here then the popup is blocked
            const errorMessage = error instanceof Error ? error.message : String(error);
            trackError('passport', 'confirmationPopupDenied', new Error(errorMessage));
            this.overlay = new Overlay(this.config.popupOverlayOptions, true);
        }
        this.overlay.append(() => {
            try {
                this.confirmationWindow?.close();
                this.confirmationWindow = openPopupCenter({
                    url: this.getHref('loading'),
                    title: CONFIRMATION_WINDOW_TITLE,
                    width: this.popupOptions?.width || CONFIRMATION_WINDOW_WIDTH,
                    height: this.popupOptions?.height || CONFIRMATION_WINDOW_HEIGHT,
                });
            }
            catch { /* Empty */ }
        }, () => {
            this.overlayClosed = true;
            this.closeWindow();
        });
    }
    closeWindow() {
        this.confirmationWindow?.close();
        this.overlay?.remove();
        this.overlay = undefined;
    }
    showConfirmationScreen(href, messageHandler, resolve) {
        // If popup blocked, the confirmation window will not exist
        if (this.confirmationWindow) {
            this.confirmationWindow.location.href = href;
        }
        // This indicates the user closed the overlay so the transaction should be rejected
        if (!this.overlay) {
            this.overlayClosed = false;
            resolve({ confirmed: false });
            return;
        }
        // https://stackoverflow.com/questions/9388380/capture-the-close-event-of-popup-window-in-javascript/48240128#48240128
        const timerCallback = () => {
            if (this.confirmationWindow?.closed || this.overlayClosed) {
                clearInterval(this.timer);
                window.removeEventListener('message', messageHandler);
                resolve({ confirmed: false });
                this.overlayClosed = false;
                this.confirmationWindow = undefined;
            }
        };
        this.timer = setInterval(timerCallback, CONFIRMATION_WINDOW_CLOSED_POLLING_DURATION);
        this.overlay.update(() => this.recreateConfirmationWindow(href, timerCallback));
    }
    recreateConfirmationWindow(href, timerCallback) {
        try {
            // Clears and recreates the timer to ensure when the confirmation window
            // is closed and recreated the transaction is not rejected.
            clearInterval(this.timer);
            this.confirmationWindow?.close();
            this.confirmationWindow = openPopupCenter({
                url: href,
                title: CONFIRMATION_WINDOW_TITLE,
                width: this.popupOptions?.width || CONFIRMATION_WINDOW_WIDTH,
                height: this.popupOptions?.height || CONFIRMATION_WINDOW_HEIGHT,
            });
            this.timer = setInterval(timerCallback, CONFIRMATION_WINDOW_CLOSED_POLLING_DURATION);
        }
        catch { /* Empty */ }
    }
}

var RelayerTransactionStatus;
(function (RelayerTransactionStatus) {
    RelayerTransactionStatus["PENDING"] = "PENDING";
    RelayerTransactionStatus["SUBMITTED"] = "SUBMITTED";
    RelayerTransactionStatus["SUCCESSFUL"] = "SUCCESSFUL";
    RelayerTransactionStatus["REVERTED"] = "REVERTED";
    RelayerTransactionStatus["FAILED"] = "FAILED";
    RelayerTransactionStatus["CANCELLED"] = "CANCELLED";
})(RelayerTransactionStatus || (RelayerTransactionStatus = {}));
var ProviderEvent;
(function (ProviderEvent) {
    ProviderEvent["ACCOUNTS_CHANGED"] = "accountsChanged";
})(ProviderEvent || (ProviderEvent = {}));

class TypedEventEmitter {
    emitter = new EventEmitter();
    emit(eventName, ...eventArg) {
        this.emitter.emit(eventName, ...eventArg);
    }
    on(eventName, handler) {
        this.emitter.on(eventName, handler);
    }
    removeListener(eventName, handler) {
        this.emitter.removeListener(eventName, handler);
    }
}

const SIGNATURE_WEIGHT = 1; // Weight of a single signature in the multi-sig
const TRANSACTION_SIGNATURE_THRESHOLD = 1; // Total required weight in the multi-sig for a transaction
const PACKED_SIGNATURE_THRESHOLD = 2; // Total required weight in the multi-sig for data signing
const ETH_SIGN_FLAG = '02';
const ETH_SIGN_PREFIX = '\x19\x01';
const META_TRANSACTIONS_TYPE = `tuple(
  bool delegateCall,
  bool revertOnError,
  uint256 gasLimit,
  address target,
  uint256 value,
  bytes data
)[]`;
const getNormalisedTransactions = (txs) => txs.map((t) => ({
    delegateCall: t.delegateCall === true,
    revertOnError: t.revertOnError === true,
    gasLimit: t.gasLimit ?? constants.Zero,
    target: t.to ?? constants.AddressZero,
    value: t.value ?? constants.Zero,
    data: t.data ?? [],
}));
const digestOfTransactionsAndNonce = (nonce, normalisedTransactions) => {
    const packMetaTransactionsNonceData = utils$1.defaultAbiCoder.encode(['uint256', META_TRANSACTIONS_TYPE], [nonce, normalisedTransactions]);
    return utils$1.keccak256(packMetaTransactionsNonceData);
};
const encodedTransactions = (normalisedTransactions) => utils$1.defaultAbiCoder.encode([META_TRANSACTIONS_TYPE], [normalisedTransactions]);
const getNonce = async (rpcProvider, smartContractWalletAddress) => {
    try {
        const contract = new Contract(smartContractWalletAddress, walletContracts.mainModule.abi, rpcProvider);
        const result = await contract.nonce();
        if (result instanceof BigNumber) {
            return result;
        }
    }
    catch (error) {
        if (error instanceof Error
            && 'code' in error
            && error.code === errors.CALL_EXCEPTION) {
            // The most likely reason for a CALL_EXCEPTION is that the smart contract wallet
            // has not been deployed yet, so we should default to a nonce of 0.
            return BigNumber.from(0);
        }
        throw error;
    }
    throw new Error('Unexpected result from contract.nonce() call.');
};
const encodeMessageSubDigest = (chainId, walletAddress, digest) => (utils$1.solidityPack(['string', 'uint256', 'address', 'bytes32'], [ETH_SIGN_PREFIX, chainId, walletAddress, digest]));
const signMetaTransactions = async (metaTransactions, nonce, chainId, walletAddress, signer) => {
    const normalisedMetaTransactions = getNormalisedTransactions(metaTransactions);
    // Get the hash
    const digest = digestOfTransactionsAndNonce(nonce, normalisedMetaTransactions);
    const completePayload = encodeMessageSubDigest(chainId, walletAddress, digest);
    const hash = utils$1.keccak256(completePayload);
    // Sign the digest
    const hashArray = utils$1.arrayify(hash);
    const startTime = performance.now();
    const ethsigNoType = await signer.signMessage(hashArray);
    trackDuration('passport', 'magicSignMessageGetSignedMetaTransactions', Math.round(performance.now() - startTime));
    const signedDigest = `${ethsigNoType}${ETH_SIGN_FLAG}`;
    // Add metadata
    const encodedSignature = v1.signature.encodeSignature({
        version: 1,
        threshold: TRANSACTION_SIGNATURE_THRESHOLD,
        signers: [
            {
                isDynamic: false,
                unrecovered: true,
                weight: SIGNATURE_WEIGHT,
                signature: signedDigest,
            },
        ],
    });
    // Encode the transaction;
    const walletInterface = new utils$1.Interface(walletContracts.mainModule.abi);
    return walletInterface.encodeFunctionData(walletInterface.getFunction('execute'), [
        normalisedMetaTransactions,
        nonce,
        encodedSignature,
    ]);
};
const decodeRelayerSignature = (relayerSignature) => {
    const signatureWithThreshold = `0x0000${relayerSignature}`;
    return v1.signature.decodeSignature(signatureWithThreshold);
};
const packSignatures = (EOASignature, EOAAddress, relayerSignature) => {
    const signedDigest = `${EOASignature}${ETH_SIGN_FLAG}`;
    // Combine the relayer and user signatures; sort by address to match the imageHash order
    const { signers: relayerSigners } = decodeRelayerSignature(relayerSignature);
    const combinedSigners = [
        ...relayerSigners,
        {
            isDynamic: false,
            unrecovered: true,
            weight: SIGNATURE_WEIGHT,
            signature: signedDigest,
            address: EOAAddress,
        },
    ];
    const sortedSigners = combinedSigners.sort((a, b) => {
        const bigA = BigNumber.from(a.address);
        const bigB = BigNumber.from(b.address);
        if (bigA.lte(bigB)) {
            return -1;
        }
        if (bigA.eq(bigB)) {
            return 0;
        }
        return 1;
    });
    return v1.signature.encodeSignature({
        version: 1,
        threshold: PACKED_SIGNATURE_THRESHOLD,
        signers: sortedSigners,
    });
};
const signAndPackTypedData = async (typedData, relayerSignature, chainId, walletAddress, signer) => {
    // Ethers auto-generates the EIP712Domain type in the TypedDataEncoder, and so it needs to be removed
    const types = { ...typedData.types };
    // @ts-ignore
    delete types.EIP712Domain;
    // Hash the EIP712 payload and generate the complete payload
    const { _TypedDataEncoder: typedDataEncoder } = utils$1;
    const typedDataHash = typedDataEncoder.hash(typedData.domain, types, typedData.message);
    const messageSubDigest = encodeMessageSubDigest(chainId, walletAddress, typedDataHash);
    const hash = utils$1.keccak256(messageSubDigest);
    // Sign the sub digest
    // https://github.com/immutable/wallet-contracts/blob/7824b5f24b2e0eb2dc465ecb5cd71f3984556b73/src/contracts/modules/commons/ModuleAuth.sol#L155
    const hashArray = utils$1.arrayify(hash);
    const startTime = performance.now();
    const eoaSignature = await signer.signMessage(hashArray);
    trackDuration('passport', 'magicSignMessageTypedData', Math.round(performance.now() - startTime));
    const eoaAddress = await signer.getAddress();
    return packSignatures(eoaSignature, eoaAddress, relayerSignature);
};
const signERC191Message = async (chainId, payload, signer, walletAddress) => {
    // Generate digest
    const digest = utils$1.hashMessage(payload);
    // Generate subDigest
    const subDigest = encodeMessageSubDigest(chainId, walletAddress, digest);
    const subDigestHash = utils$1.keccak256(subDigest);
    const subDigestHashArray = utils$1.arrayify(subDigestHash);
    return signer.signMessage(subDigestHashArray);
};
const getEip155ChainId = (chainId) => `eip155:${chainId}`;

class RelayerClient {
    config;
    rpcProvider;
    authManager;
    constructor({ config, rpcProvider, authManager }) {
        this.config = config;
        this.rpcProvider = rpcProvider;
        this.authManager = authManager;
    }
    async postToRelayer(request) {
        const body = {
            id: 1,
            jsonrpc: '2.0',
            ...request,
        };
        const user = await this.authManager.getUserZkEvm();
        const startTime = performance.now();
        const response = await fetch(`${this.config.relayerUrl}/v1/transactions`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${user.accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        trackDuration('passport', 'postToRelayer', Math.round(performance.now() - startTime), { rpcMethod: request.method });
        const jsonResponse = await response.json();
        if (jsonResponse.error) {
            throw jsonResponse.error;
        }
        return jsonResponse;
    }
    async ethSendTransaction(to, data) {
        const { chainId } = await this.rpcProvider.detectNetwork();
        const payload = {
            method: 'eth_sendTransaction',
            params: [{
                    to,
                    data,
                    chainId: getEip155ChainId(chainId),
                }],
        };
        const { result } = await this.postToRelayer(payload);
        return result;
    }
    async imGetTransactionByHash(hash) {
        const payload = {
            method: 'im_getTransactionByHash',
            params: [hash],
        };
        const { result } = await this.postToRelayer(payload);
        return result;
    }
    async imGetFeeOptions(userAddress, data) {
        const { chainId } = await this.rpcProvider.detectNetwork();
        const payload = {
            method: 'im_getFeeOptions',
            params: [{
                    userAddress,
                    data,
                    chainId: getEip155ChainId(chainId),
                }],
        };
        const { result } = await this.postToRelayer(payload);
        return result;
    }
    async imSignTypedData(address, eip712Payload) {
        const { chainId } = await this.rpcProvider.detectNetwork();
        const payload = {
            method: 'im_signTypedData',
            params: [{
                    address,
                    eip712Payload,
                    chainId: getEip155ChainId(chainId),
                }],
        };
        const { result } = await this.postToRelayer(payload);
        return result;
    }
    async imSign(address, message) {
        const { chainId } = await this.rpcProvider.detectNetwork();
        const payload = {
            method: 'im_sign',
            params: [{
                    address,
                    message,
                    chainId: getEip155ChainId(chainId),
                }],
        };
        const { result } = await this.postToRelayer(payload);
        return result;
    }
}

/**
 * ProviderErrors should take priority over RpcErrorCodes
 * https://eips.ethereum.org/EIPS/eip-1193#provider-errors
 * https://eips.ethereum.org/EIPS/eip-1474#error-codes
 */
var ProviderErrorCode;
(function (ProviderErrorCode) {
    ProviderErrorCode[ProviderErrorCode["USER_REJECTED_REQUEST"] = 4001] = "USER_REJECTED_REQUEST";
    ProviderErrorCode[ProviderErrorCode["UNAUTHORIZED"] = 4100] = "UNAUTHORIZED";
    ProviderErrorCode[ProviderErrorCode["UNSUPPORTED_METHOD"] = 4200] = "UNSUPPORTED_METHOD";
    ProviderErrorCode[ProviderErrorCode["DISCONNECTED"] = 4900] = "DISCONNECTED";
})(ProviderErrorCode || (ProviderErrorCode = {}));
var RpcErrorCode;
(function (RpcErrorCode) {
    RpcErrorCode[RpcErrorCode["RPC_SERVER_ERROR"] = -32000] = "RPC_SERVER_ERROR";
    RpcErrorCode[RpcErrorCode["INVALID_REQUEST"] = -32600] = "INVALID_REQUEST";
    RpcErrorCode[RpcErrorCode["METHOD_NOT_FOUND"] = -32601] = "METHOD_NOT_FOUND";
    RpcErrorCode[RpcErrorCode["INVALID_PARAMS"] = -32602] = "INVALID_PARAMS";
    RpcErrorCode[RpcErrorCode["INTERNAL_ERROR"] = -32603] = "INTERNAL_ERROR";
    RpcErrorCode[RpcErrorCode["PARSE_ERROR"] = -32700] = "PARSE_ERROR";
    RpcErrorCode[RpcErrorCode["TRANSACTION_REJECTED"] = -32003] = "TRANSACTION_REJECTED";
})(RpcErrorCode || (RpcErrorCode = {}));
class JsonRpcError extends Error {
    message;
    code;
    constructor(code, message) {
        super(message);
        this.message = message;
        this.code = code;
    }
}

const MESSAGE_TO_SIGN = 'Only sign this message from Immutable Passport';
async function registerZkEvmUser({ authManager, ethSigner, multiRollupApiClients, accessToken, rpcProvider, flow, }) {
    // Parallelize the operations that can happen concurrently
    const getAddressPromise = ethSigner.getAddress();
    getAddressPromise.then(() => flow.addEvent('endGetAddress'));
    const signRawPromise = signRaw(MESSAGE_TO_SIGN, ethSigner);
    signRawPromise.then(() => flow.addEvent('endSignRaw'));
    const detectNetworkPromise = rpcProvider.detectNetwork();
    detectNetworkPromise.then(() => flow.addEvent('endDetectNetwork'));
    const listChainsPromise = multiRollupApiClients.chainsApi.listChains();
    listChainsPromise.then(() => flow.addEvent('endListChains'));
    const [ethereumAddress, ethereumSignature, network, chainListResponse] = await Promise.all([
        getAddressPromise,
        signRawPromise,
        detectNetworkPromise,
        listChainsPromise,
    ]);
    const eipChainId = getEip155ChainId(network.chainId);
    const chainName = chainListResponse.data?.result?.find((chain) => chain.id === eipChainId)?.name;
    if (!chainName) {
        throw new JsonRpcError(RpcErrorCode.INTERNAL_ERROR, `Chain name does not exist on for chain id ${network.chainId}`);
    }
    try {
        const registrationResponse = await multiRollupApiClients.passportApi.createCounterfactualAddressV2({
            chainName,
            createCounterfactualAddressRequest: {
                ethereum_address: ethereumAddress,
                ethereum_signature: ethereumSignature,
            },
        }, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        flow.addEvent('endCreateCounterfactualAddress');
        authManager.forceUserRefreshInBackground();
        return registrationResponse.data.counterfactual_address;
    }
    catch (error) {
        throw new JsonRpcError(RpcErrorCode.INTERNAL_ERROR, `Failed to create counterfactual address: ${error}`);
    }
}

const transactionRejectedCrossSdkBridgeError = 'Transaction requires confirmation but this functionality is not'
    + ' supported in this environment. Please contact Immutable support if you need to enable this feature.';
const convertBigNumberishToString = (value) => BigNumber.from(value).toString();
const transformGuardianTransactions = (txs) => {
    try {
        return txs.map((t) => ({
            delegateCall: t.delegateCall === true,
            revertOnError: t.revertOnError === true,
            gasLimit: t.gasLimit ? convertBigNumberishToString(t.gasLimit) : '0',
            target: t.to ?? ethers.constants.AddressZero,
            value: t.value ? convertBigNumberishToString(t.value) : '0',
            data: t.data ? t.data.toString() : '0x',
        }));
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, `Transaction failed to parsing: ${errorMessage}`);
    }
};
class GuardianClient {
    guardianApi;
    confirmationScreen;
    crossSdkBridgeEnabled;
    authManager;
    constructor({ confirmationScreen, config, authManager, guardianApi, }) {
        this.confirmationScreen = confirmationScreen;
        this.crossSdkBridgeEnabled = config.crossSdkBridgeEnabled;
        this.guardianApi = guardianApi;
        this.authManager = authManager;
    }
    /**
     * Open confirmation screen and close it automatically if the
     * underlying task fails.
     */
    withConfirmationScreen(popupWindowSize) {
        return (task) => this.withConfirmationScreenTask(popupWindowSize)(task)();
    }
    withConfirmationScreenTask(popupWindowSize) {
        return (task) => async () => {
            this.confirmationScreen.loading(popupWindowSize);
            try {
                return await task();
            }
            catch (err) {
                this.confirmationScreen.closeWindow();
                throw err;
            }
        };
    }
    withDefaultConfirmationScreenTask(task) {
        return this.withConfirmationScreenTask()(task);
    }
    async evaluateImxTransaction({ payloadHash }) {
        const finallyFn = () => {
            this.confirmationScreen.closeWindow();
        };
        const user = await this.authManager.getUserImx();
        const headers = { Authorization: `Bearer ${user.accessToken}` };
        const transactionRes = await retryWithDelay(async () => this.guardianApi.getTransactionByID({
            transactionID: payloadHash,
            chainType: 'starkex',
        }, { headers }), { finallyFn });
        if (!transactionRes.data.id) {
            throw new Error("Transaction doesn't exists");
        }
        const evaluateImxRes = await this.guardianApi.evaluateTransaction({
            id: payloadHash,
            transactionEvaluationRequest: {
                chainType: 'starkex',
            },
        }, { headers });
        const { confirmationRequired } = evaluateImxRes.data;
        if (confirmationRequired) {
            if (this.crossSdkBridgeEnabled) {
                throw new Error(transactionRejectedCrossSdkBridgeError);
            }
            const confirmationResult = await this.confirmationScreen.requestConfirmation(payloadHash, user.imx.ethAddress, index$1.TransactionApprovalRequestChainTypeEnum.Starkex);
            if (!confirmationResult.confirmed) {
                throw new Error('Transaction rejected by user');
            }
        }
        else {
            this.confirmationScreen.closeWindow();
        }
    }
    async evaluateEVMTransaction({ chainId, nonce, metaTransactions, }) {
        const user = await this.authManager.getUserZkEvm();
        const headers = { Authorization: `Bearer ${user.accessToken}` };
        const guardianTransactions = transformGuardianTransactions(metaTransactions);
        try {
            const response = await this.guardianApi.evaluateTransaction({
                id: 'evm',
                transactionEvaluationRequest: {
                    chainType: 'evm',
                    chainId,
                    transactionData: {
                        nonce,
                        userAddress: user.zkEvm.ethAddress,
                        metaTransactions: guardianTransactions,
                    },
                },
            }, { headers });
            return response.data;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new JsonRpcError(RpcErrorCode.INTERNAL_ERROR, `Transaction failed to validate with error: ${errorMessage}`);
        }
    }
    async validateEVMTransaction({ chainId, nonce, metaTransactions, }) {
        const transactionEvaluationResponse = await this.evaluateEVMTransaction({
            chainId,
            nonce,
            metaTransactions,
        });
        const { confirmationRequired, transactionId } = transactionEvaluationResponse;
        if (confirmationRequired && this.crossSdkBridgeEnabled) {
            throw new JsonRpcError(RpcErrorCode.TRANSACTION_REJECTED, transactionRejectedCrossSdkBridgeError);
        }
        if (confirmationRequired && !!transactionId) {
            const user = await this.authManager.getUserZkEvm();
            const confirmationResult = await this.confirmationScreen.requestConfirmation(transactionId, user.zkEvm.ethAddress, index$1.TransactionApprovalRequestChainTypeEnum.Evm, chainId);
            if (!confirmationResult.confirmed) {
                throw new JsonRpcError(RpcErrorCode.TRANSACTION_REJECTED, 'Transaction rejected by user');
            }
        }
        else {
            this.confirmationScreen.closeWindow();
        }
    }
    async handleEIP712MessageEvaluation({ chainID, payload }) {
        try {
            const user = await this.authManager.getUserZkEvm();
            if (user === null) {
                throw new JsonRpcError(ProviderErrorCode.UNAUTHORIZED, 'User not logged in. Please log in first.');
            }
            const messageEvalResponse = await this.guardianApi.evaluateMessage({ messageEvaluationRequest: { chainID, payload } }, { headers: { Authorization: `Bearer ${user.accessToken}` } });
            return messageEvalResponse.data;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new JsonRpcError(RpcErrorCode.INTERNAL_ERROR, `Message failed to validate with error: ${errorMessage}`);
        }
    }
    async evaluateEIP712Message({ chainID, payload }) {
        const { messageId, confirmationRequired } = await this.handleEIP712MessageEvaluation({ chainID, payload });
        if (confirmationRequired && this.crossSdkBridgeEnabled) {
            throw new JsonRpcError(RpcErrorCode.TRANSACTION_REJECTED, transactionRejectedCrossSdkBridgeError);
        }
        if (confirmationRequired && !!messageId) {
            const user = await this.authManager.getUserZkEvm();
            const confirmationResult = await this.confirmationScreen.requestMessageConfirmation(messageId, user.zkEvm.ethAddress, 'eip712');
            if (!confirmationResult.confirmed) {
                throw new JsonRpcError(RpcErrorCode.TRANSACTION_REJECTED, 'Signature rejected by user');
            }
        }
        else {
            this.confirmationScreen.closeWindow();
        }
    }
    async handleERC191MessageEvaluation({ chainID, payload }) {
        try {
            const user = await this.authManager.getUserZkEvm();
            if (user === null) {
                throw new JsonRpcError(ProviderErrorCode.UNAUTHORIZED, 'User not logged in. Please log in first.');
            }
            const messageEvalResponse = await this.guardianApi.evaluateErc191Message({
                eRC191MessageEvaluationRequest: {
                    chainID: getEip155ChainId(chainID),
                    payload,
                },
            }, { headers: { Authorization: `Bearer ${user.accessToken}` } });
            return messageEvalResponse.data;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new JsonRpcError(RpcErrorCode.INTERNAL_ERROR, `Message failed to validate with error: ${errorMessage}`);
        }
    }
    async evaluateERC191Message({ chainID, payload }) {
        const { messageId, confirmationRequired } = await this.handleERC191MessageEvaluation({ chainID, payload });
        if (confirmationRequired && this.crossSdkBridgeEnabled) {
            throw new JsonRpcError(RpcErrorCode.TRANSACTION_REJECTED, transactionRejectedCrossSdkBridgeError);
        }
        if (confirmationRequired && !!messageId) {
            const user = await this.authManager.getUserZkEvm();
            const confirmationResult = await this.confirmationScreen.requestMessageConfirmation(messageId, user.zkEvm.ethAddress, 'erc191');
            if (!confirmationResult.confirmed) {
                throw new JsonRpcError(RpcErrorCode.TRANSACTION_REJECTED, 'Signature rejected by user');
            }
        }
        else {
            this.confirmationScreen.closeWindow();
        }
    }
}

const MAX_TRANSACTION_HASH_RETRIEVAL_RETRIES = 30;
const TRANSACTION_HASH_RETRIEVAL_WAIT = 1000;
const getFeeOption = async (metaTransaction, walletAddress, relayerClient) => {
    const normalisedMetaTransaction = getNormalisedTransactions([
        metaTransaction,
    ]);
    const transactions = encodedTransactions(normalisedMetaTransaction);
    const feeOptions = await relayerClient.imGetFeeOptions(walletAddress, transactions);
    const imxFeeOption = feeOptions.find((feeOption) => feeOption.tokenSymbol === 'IMX');
    if (!imxFeeOption) {
        throw new Error('Failed to retrieve fees for IMX token');
    }
    return imxFeeOption;
};
/**
 * Prepares the meta transactions array to be signed by estimating the fee and
 * getting the nonce from the smart wallet.
 *
 */
const buildMetaTransactions = async (transactionRequest, rpcProvider, relayerClient, zkevmAddress) => {
    if (!transactionRequest.to) {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, 'eth_sendTransaction requires a "to" field');
    }
    const metaTransaction = {
        to: transactionRequest.to,
        data: transactionRequest.data,
        nonce: BigNumber.from(0), // NOTE: We don't need a valid nonce to estimate the fee
        value: transactionRequest.value,
        revertOnError: true,
    };
    // Estimate the fee and get the nonce from the smart wallet
    const [nonce, feeOption] = await Promise.all([
        getNonce(rpcProvider, zkevmAddress),
        getFeeOption(metaTransaction, zkevmAddress, relayerClient),
    ]);
    // Build the meta transactions array with a valid nonce and fee transaction
    const metaTransactions = [
        {
            ...metaTransaction,
            nonce,
        },
    ];
    // Add a fee transaction if the fee is non-zero
    const feeValue = BigNumber.from(feeOption.tokenPrice);
    if (!feeValue.isZero()) {
        metaTransactions.push({
            nonce,
            to: feeOption.recipientAddress,
            value: feeValue,
            revertOnError: true,
        });
    }
    return metaTransactions;
};
const pollRelayerTransaction = async (relayerClient, relayerId, flow) => {
    const retrieveRelayerTransaction = async () => {
        const tx = await relayerClient.imGetTransactionByHash(relayerId);
        // NOTE: The transaction hash is only available from the Relayer once the
        // transaction is actually submitted onchain. Hence we need to poll the
        // Relayer get transaction endpoint until the status transitions to one that
        // has the hash available.
        if (tx.status === RelayerTransactionStatus.PENDING) {
            throw new Error();
        }
        return tx;
    };
    const relayerTransaction = await retryWithDelay(retrieveRelayerTransaction, {
        retries: MAX_TRANSACTION_HASH_RETRIEVAL_RETRIES,
        interval: TRANSACTION_HASH_RETRIEVAL_WAIT,
        finalErr: new JsonRpcError(RpcErrorCode.RPC_SERVER_ERROR, 'transaction hash not generated in time'),
    });
    flow.addEvent('endRetrieveRelayerTransaction');
    if (![
        RelayerTransactionStatus.SUBMITTED,
        RelayerTransactionStatus.SUCCESSFUL,
    ].includes(relayerTransaction.status)) {
        let errorMessage = `Transaction failed to submit with status ${relayerTransaction.status}.`;
        if (relayerTransaction.statusMessage) {
            errorMessage += ` Error message: ${relayerTransaction.statusMessage}`;
        }
        throw new JsonRpcError(RpcErrorCode.RPC_SERVER_ERROR, errorMessage);
    }
    return relayerTransaction;
};
const prepareAndSignTransaction = async ({ transactionRequest, ethSigner, rpcProvider, guardianClient, relayerClient, zkEvmAddress, flow, }) => {
    const { chainId } = await rpcProvider.detectNetwork();
    const chainIdBigNumber = BigNumber.from(chainId);
    flow.addEvent('endDetectNetwork');
    const metaTransactions = await buildMetaTransactions(transactionRequest, rpcProvider, relayerClient, zkEvmAddress);
    flow.addEvent('endBuildMetaTransactions');
    const { nonce } = metaTransactions[0];
    if (!nonce) {
        throw new Error('Failed to retrieve nonce from the smart wallet');
    }
    // Parallelize the validation and signing of the transaction
    // without waiting for the validation to complete
    const validateTransaction = async () => {
        await guardianClient.validateEVMTransaction({
            chainId: getEip155ChainId(chainId),
            nonce: convertBigNumberishToString(nonce),
            metaTransactions,
        });
        flow.addEvent('endValidateEVMTransaction');
    };
    // NOTE: We sign again because we now are adding the fee transaction, so the
    // whole payload is different and needs a new signature.
    const signTransaction = async () => {
        const signed = await signMetaTransactions(metaTransactions, nonce, chainIdBigNumber, zkEvmAddress, ethSigner);
        flow.addEvent('endGetSignedMetaTransactions');
        return signed;
    };
    const [, signedTransactions] = await Promise.all([
        validateTransaction(),
        signTransaction(),
    ]);
    const relayerId = await relayerClient.ethSendTransaction(zkEvmAddress, signedTransactions);
    flow.addEvent('endRelayerSendTransaction');
    return { signedTransactions, relayerId, nonce };
};
const buildMetaTransactionForEjection = async (transactionRequest) => {
    if (!transactionRequest.to) {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, 'im_signEjectionTransaction requires a "to" field');
    }
    if (!transactionRequest.nonce) {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, 'im_signEjectionTransaction requires a "nonce" field');
    }
    if (!transactionRequest.chainId) {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, 'im_signEjectionTransaction requires a "chainId" field');
    }
    const metaTransaction = {
        to: transactionRequest.to,
        data: transactionRequest.data,
        nonce: transactionRequest.nonce,
        value: transactionRequest.value,
        revertOnError: true,
    };
    return [metaTransaction];
};
const prepareAndSignEjectionTransaction = async ({ transactionRequest, ethSigner, zkEvmAddress, flow, }) => {
    const metaTransaction = await buildMetaTransactionForEjection(transactionRequest);
    flow.addEvent('endBuildMetaTransactions');
    const signedTransaction = await signMetaTransactions(metaTransaction, transactionRequest.nonce, BigNumber.from(transactionRequest.chainId), zkEvmAddress, ethSigner);
    flow.addEvent('endGetSignedMetaTransactions');
    return {
        to: zkEvmAddress,
        data: signedTransaction,
        chainId: getEip155ChainId(transactionRequest.chainId),
    };
};

const sendTransaction = async ({ params, ethSigner, rpcProvider, relayerClient, guardianClient, zkEvmAddress, flow, }) => {
    const transactionRequest = params[0];
    const { relayerId } = await prepareAndSignTransaction({
        transactionRequest,
        ethSigner,
        rpcProvider,
        guardianClient,
        relayerClient,
        zkEvmAddress,
        flow,
    });
    const { hash } = await pollRelayerTransaction(relayerClient, relayerId, flow);
    return hash;
};

const REQUIRED_TYPED_DATA_PROPERTIES = ['types', 'domain', 'primaryType', 'message'];
const isValidTypedDataPayload = (typedData) => (REQUIRED_TYPED_DATA_PROPERTIES.every((key) => key in typedData));
const transformTypedData = (typedData, chainId) => {
    let transformedTypedData;
    if (typeof typedData === 'string') {
        try {
            transformedTypedData = JSON.parse(typedData);
        }
        catch (err) {
            throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, `Failed to parse typed data JSON: ${err}`);
        }
    }
    else if (typeof typedData === 'object') {
        transformedTypedData = typedData;
    }
    else {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, `Invalid typed data argument: ${typedData}`);
    }
    if (!isValidTypedDataPayload(transformedTypedData)) {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, `Invalid typed data argument. The following properties are required: ${REQUIRED_TYPED_DATA_PROPERTIES.join(', ')}`);
    }
    const providedChainId = transformedTypedData.domain?.chainId;
    if (providedChainId) {
        // domain.chainId (if defined) can be a number, string, or hex value, but the relayer & guardian only accept a number.
        if (typeof providedChainId === 'string') {
            if (providedChainId.startsWith('0x')) {
                transformedTypedData.domain.chainId = parseInt(providedChainId, 16);
            }
            else {
                transformedTypedData.domain.chainId = parseInt(providedChainId, 10);
            }
        }
        if (transformedTypedData.domain.chainId !== chainId) {
            throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, `Invalid chainId, expected ${chainId}`);
        }
    }
    return transformedTypedData;
};
const signTypedDataV4 = async ({ params, method, ethSigner, rpcProvider, relayerClient, guardianClient, flow, }) => {
    const fromAddress = params[0];
    const typedDataParam = params[1];
    if (!fromAddress || !typedDataParam) {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, `${method} requires an address and a typed data JSON`);
    }
    const { chainId } = await rpcProvider.detectNetwork();
    const typedData = transformTypedData(typedDataParam, chainId);
    flow.addEvent('endDetectNetwork');
    await guardianClient.evaluateEIP712Message({ chainID: String(chainId), payload: typedData });
    flow.addEvent('endValidateMessage');
    const relayerSignature = await relayerClient.imSignTypedData(fromAddress, typedData);
    flow.addEvent('endRelayerSignTypedData');
    const signature = await signAndPackTypedData(typedData, relayerSignature, BigNumber.from(chainId), fromAddress, ethSigner);
    flow.addEvent('getSignedTypedData');
    return signature;
};

const hexToString = (hex) => {
    if (!hex)
        return hex;
    try {
        const stripped = utils$1.stripZeros(utils$1.arrayify(hex));
        return utils$1.toUtf8String(stripped);
    }
    catch (e) {
        return hex;
    }
};

const personalSign = async ({ params, ethSigner, zkEvmAddress, rpcProvider, guardianClient, relayerClient, flow, }) => {
    const message = params[0];
    const fromAddress = params[1];
    if (!fromAddress || !message) {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, 'personal_sign requires an address and a message');
    }
    if (fromAddress.toLowerCase() !== zkEvmAddress.toLowerCase()) {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, 'personal_sign requires the signer to be the from address');
    }
    // Convert message into a string if it's a hex
    const payload = hexToString(message);
    const { chainId } = await rpcProvider.detectNetwork();
    flow.addEvent('endDetectNetwork');
    const chainIdBigNumber = BigNumber.from(chainId);
    // Sign the message with the EOA without blocking
    const eoaSignaturePromise = signERC191Message(chainIdBigNumber, payload, ethSigner, fromAddress);
    eoaSignaturePromise.then(() => flow.addEvent('endEOASignature'));
    await guardianClient.evaluateERC191Message({ chainID: chainId, payload });
    flow.addEvent('endEvaluateERC191Message');
    const [eoaSignature, relayerSignature] = await Promise.all([
        eoaSignaturePromise,
        relayerClient.imSign(fromAddress, payload),
    ]);
    flow.addEvent('endRelayerSign');
    const eoaAddress = await ethSigner.getAddress();
    flow.addEvent('endGetEOAAddress');
    return packSignatures(eoaSignature, eoaAddress, relayerSignature);
};

// For session activity checks, always use production
// even for sandbox.
const PROD_API = 'https://api.immutable.com';
const SANDBOX_API = 'https://api.sandbox.immutable.com';
const CHECK_PATH = '/v1/sdk/session-activity/check';
const getBaseUrl = (environment) => {
    switch (environment) {
        case Environment.SANDBOX:
            return SANDBOX_API;
        case Environment.PRODUCTION:
            return PROD_API;
        default:
            throw new Error('Environment not supported');
    }
};
let client;
const setupClient = (environment) => {
    if (client) {
        return;
    }
    client = globalAxios.create({
        baseURL: getBaseUrl(environment),
    });
};
async function get(queries) {
    if (!client) {
        throw new Error('Client not initialised');
    }
    // pass queries as query string
    return client
        .get(CHECK_PATH, {
        params: queries,
    })
        .then((res) => res.data)
        .catch((error) => {
        if (error.response.status === 404) {
            return undefined;
        }
        throw error;
    });
}

function errorBoundary(fn, fallbackResult) {
    return (...args) => {
        try {
            // Execute the original function
            const result = fn(...args);
            if (result instanceof Promise) {
                // Silent fail for now, in future
                // we can send errors to a logging service
                return result.catch((error) => {
                    if (error instanceof Error) {
                        trackError('passport', 'sessionActivityError', error);
                    }
                    return fallbackResult;
                });
            }
            return result;
        }
        catch (error) {
            if (error instanceof Error) {
                trackError('passport', 'sessionActivityError', error);
            }
            // As above, fail silently for now
            return fallbackResult;
        }
    };
}

// Local Storage Keys
const { getItem, setItem } = utils.localStorage;
const SESSION_ACTIVITY_COUNT_KEY = 'sessionActivitySendCount';
const SESSION_ACTIVITY_DAY_KEY = 'sessionActivityDate';
// Maintain a few local counters for session activity
let checkCount = 0;
let sendCount = 0;
let currentSessionTrackCall = false;
// Sync sendCount to localStorage
const syncSendCount = () => {
    sendCount = getItem(SESSION_ACTIVITY_COUNT_KEY) || 0;
    const sendDay = getItem(SESSION_ACTIVITY_DAY_KEY);
    // If no day, set count to zero. If not today, reset sendCount to 0
    const today = new Date().toISOString().split('T')[0];
    if (!sendDay || sendDay !== today) {
        sendCount = 0;
    }
    setItem(SESSION_ACTIVITY_DAY_KEY, today);
    setItem(SESSION_ACTIVITY_COUNT_KEY, sendCount);
};
// Run as soon as module initialised.
syncSendCount();
const incrementSendCount = () => {
    syncSendCount();
    sendCount++;
    setItem(SESSION_ACTIVITY_COUNT_KEY, sendCount);
    // Reset checkCount to zero on sending
    checkCount = 0;
};
// Fix no-promise-executor-return
const wait = async (seconds) => new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000);
});
const trackSessionActivityFn = async (args) => {
    // Use an existing flow if one is provided, or create a new one
    const flow = args.flow || trackFlow('passport', 'sendSessionActivity');
    // If there is already a tracking call in progress, do nothing
    if (currentSessionTrackCall) {
        flow.addEvent('Existing Delay Early Exit');
        return;
    }
    currentSessionTrackCall = true;
    const { sendTransaction, environment } = args;
    if (!sendTransaction) {
        throw new Error('No sendTransaction function provided');
    }
    // Used to set up the request client
    if (!environment) {
        throw new Error('No environment provided');
    }
    setupClient(environment);
    const clientId = args.passportClient;
    if (!clientId) {
        flow.addEvent('No Passport Client ID');
        throw new Error('No Passport Client ID provided');
    }
    const from = args.walletAddress;
    if (!from) {
        flow.addEvent('No Passport Wallet Address');
        throw new Error('No wallet address');
    }
    //   Return type of get
    let details;
    // Make the API call
    try {
        flow.addEvent('Fetching details');
        details = await get({
            clientId,
            wallet: from,
            checkCount,
            sendCount,
        });
        checkCount++;
        flow.addEvent('Fetched details', { checkCount });
        if (!details) {
            flow.addEvent('No details found');
            return;
        }
    }
    catch (error) {
        flow.addEvent('Failed to fetch details');
        throw new Error('Failed to get details', { cause: error });
    }
    if (details && details.contractAddress && details.functionName) {
        const contractInterface = () => new utils$1.Interface([`function ${details.functionName}()`]);
        const data = contractInterface().encodeFunctionData(details.functionName);
        const to = details.contractAddress;
        // If transaction payload, send transaction
        try {
            flow.addEvent('Start Sending Transaction');
            const tx = await args.sendTransaction([{ to, from, data }], flow);
            incrementSendCount();
            flow.addEvent('Transaction Sent', { tx });
        }
        catch (error) {
            flow.addEvent('Failed to send Transaction');
            const err = new Error('Failed to send transaction', { cause: error });
            trackError('passport', 'sessionActivityError', err);
        }
    }
    // if delay, perform delay.
    if (details && details.delay && details.delay > 0) {
        flow.addEvent('Delaying Transaction', { delay: details.delay });
        await wait(details.delay);
        setTimeout(() => {
            flow.addEvent('Retrying after Delay');
            currentSessionTrackCall = false;
            // eslint-disable-next-line
            trackSessionWrapper({ ...args, flow });
        }, 0);
    }
};
// Wrapper design to ensure that after track function is called, current session Track call is false.
const trackSessionWrapper = (args) => errorBoundary(trackSessionActivityFn)(args).then(() => {
    currentSessionTrackCall = false;
});
const trackSessionActivity = trackSessionWrapper;

const sendDeployTransactionAndPersonalSign = async ({ params, ethSigner, rpcProvider, relayerClient, guardianClient, zkEvmAddress, flow, }) => {
    const deployTransaction = { to: zkEvmAddress, value: 0 };
    const { relayerId } = await prepareAndSignTransaction({
        transactionRequest: deployTransaction,
        ethSigner,
        rpcProvider,
        guardianClient,
        relayerClient,
        zkEvmAddress,
        flow,
    });
    return guardianClient.withConfirmationScreen()(async () => {
        const signedMessage = await personalSign({
            params,
            ethSigner,
            zkEvmAddress,
            rpcProvider,
            guardianClient,
            relayerClient,
            flow,
        });
        await pollRelayerTransaction(relayerClient, relayerId, flow);
        return signedMessage;
    });
};

const signEjectionTransaction = async ({ params, ethSigner, zkEvmAddress, flow, }) => {
    if (!params || params.length !== 1) {
        throw new JsonRpcError(RpcErrorCode.INVALID_PARAMS, 'im_signEjectionTransaction requires a singular param (hash)');
    }
    const transactionRequest = params[0];
    return await prepareAndSignEjectionTransaction({
        transactionRequest,
        ethSigner,
        zkEvmAddress,
        flow,
    });
};

const isZkEvmUser = (user) => 'zkEvm' in user;
class ZkEvmProvider {
    #authManager;
    #config;
    /**
     * intended to emit EIP-1193 events
     */
    #providerEventEmitter;
    /**
     * intended to emit internal Passport events
     */
    #passportEventEmitter;
    #guardianClient;
    #rpcProvider; // Used for read
    #magicAdapter;
    #multiRollupApiClients;
    #relayerClient;
    /**
     * This property is set during `#initialiseEthSigner` and stores the signer in a promise.
     * This property is not meant to be accessed directly, but through the
     * `#getSigner` method.
     * @see getSigner
     */
    #ethSigner;
    #signerInitialisationError;
    isPassport = true;
    constructor({ authManager, magicAdapter, config, multiRollupApiClients, passportEventEmitter, guardianClient, }) {
        this.#authManager = authManager;
        this.#magicAdapter = magicAdapter;
        this.#config = config;
        this.#guardianClient = guardianClient;
        this.#passportEventEmitter = passportEventEmitter;
        if (config.jsonRpcReferrer) {
            // StaticJsonRpcProvider by default sets the referrer as "client".
            // On Unreal 4 this errors as the browser used is expecting a valid URL.
            this.#rpcProvider = new StaticJsonRpcProvider({
                url: this.#config.zkEvmRpcUrl,
                fetchOptions: { referrer: config.jsonRpcReferrer },
            });
        }
        else {
            this.#rpcProvider = new StaticJsonRpcProvider(this.#config.zkEvmRpcUrl);
        }
        this.#relayerClient = new RelayerClient({
            config: this.#config,
            rpcProvider: this.#rpcProvider,
            authManager: this.#authManager,
        });
        this.#multiRollupApiClients = multiRollupApiClients;
        this.#providerEventEmitter = new TypedEventEmitter();
        // Automatically connect an existing user session to Passport
        this.#authManager.getUser().then((user) => {
            if (user) {
                this.#initialiseEthSigner(user);
                if (isZkEvmUser(user)) {
                    this.#callSessionActivity(user.zkEvm.ethAddress);
                }
            }
        }).catch(); // User does not exist, don't initialise an eth signer
        passportEventEmitter.on(PassportEvents.LOGGED_IN, (user) => {
            this.#initialiseEthSigner(user);
            if (isZkEvmUser(user)) {
                this.#callSessionActivity(user.zkEvm.ethAddress);
            }
        });
        passportEventEmitter.on(PassportEvents.LOGGED_OUT, this.#handleLogout);
        passportEventEmitter.on(PassportEvents.ACCOUNTS_REQUESTED, trackSessionActivity);
    }
    #handleLogout = () => {
        this.#ethSigner = undefined;
        this.#providerEventEmitter.emit(ProviderEvent.ACCOUNTS_CHANGED, []);
    };
    /**
     * This method is called by `eth_requestAccounts` and asynchronously initialises the signer.
     * The signer is stored in a promise so that it can be retrieved by the provider
     * when needed.
     *
     * If an error is thrown during initialisation, it is stored in the `signerInitialisationError`,
     * so that it doesn't result in an unhandled promise rejection.
     *
     * This error is thrown when the signer is requested through:
     * @see #getSigner
     *
     */
    #initialiseEthSigner(user) {
        const generateSigner = async () => {
            const magicRpcProvider = await this.#magicAdapter.login(user.idToken);
            const web3Provider = new Web3Provider(magicRpcProvider);
            return web3Provider.getSigner();
        };
        this.#signerInitialisationError = undefined;
        // eslint-disable-next-line no-async-promise-executor
        this.#ethSigner = new Promise(async (resolve) => {
            try {
                resolve(await generateSigner());
            }
            catch (err) {
                // Capture and store the initialization error
                this.#signerInitialisationError = err;
                resolve(undefined);
            }
        });
    }
    async #getSigner() {
        const ethSigner = await this.#ethSigner;
        // Throw the stored error if the signers failed to initialise
        if (typeof ethSigner === 'undefined') {
            if (typeof this.#signerInitialisationError !== 'undefined') {
                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                throw this.#signerInitialisationError;
            }
            throw new Error('Signer failed to initialise');
        }
        return ethSigner;
    }
    async #callSessionActivity(zkEvmAddress) {
        const sendTransactionClosure = async (params, flow) => {
            const ethSigner = await this.#getSigner();
            return await sendTransaction({
                params,
                ethSigner,
                guardianClient: this.#guardianClient,
                rpcProvider: this.#rpcProvider,
                relayerClient: this.#relayerClient,
                zkEvmAddress,
                flow,
            });
        };
        this.#passportEventEmitter.emit(PassportEvents.ACCOUNTS_REQUESTED, {
            environment: this.#config.baseConfig.environment,
            sendTransaction: sendTransactionClosure,
            walletAddress: zkEvmAddress,
            passportClient: this.#config.oidcConfiguration.clientId,
        });
    }
    // Used to get the registered zkEvm address from the User session
    async #getZkEvmAddress() {
        try {
            const user = await this.#authManager.getUser();
            if (user && isZkEvmUser(user)) {
                return user.zkEvm.ethAddress;
            }
            return undefined;
        }
        catch {
            return undefined;
        }
    }
    async #performRequest(request) {
        // This is required for sending session activity events
        switch (request.method) {
            case 'eth_requestAccounts': {
                const zkEvmAddress = await this.#getZkEvmAddress();
                if (zkEvmAddress)
                    return [zkEvmAddress];
                const flow = trackFlow('passport', 'ethRequestAccounts');
                try {
                    const user = await this.#authManager.getUserOrLogin();
                    flow.addEvent('endGetUserOrLogin');
                    if (!this.#ethSigner) {
                        this.#initialiseEthSigner(user);
                    }
                    let userZkEvmEthAddress;
                    if (!isZkEvmUser(user)) {
                        flow.addEvent('startUserRegistration');
                        const ethSigner = await this.#getSigner();
                        flow.addEvent('ethSignerResolved');
                        userZkEvmEthAddress = await registerZkEvmUser({
                            ethSigner,
                            authManager: this.#authManager,
                            multiRollupApiClients: this.#multiRollupApiClients,
                            accessToken: user.accessToken,
                            rpcProvider: this.#rpcProvider,
                            flow,
                        });
                        flow.addEvent('endUserRegistration');
                    }
                    else {
                        userZkEvmEthAddress = user.zkEvm.ethAddress;
                    }
                    this.#providerEventEmitter.emit(ProviderEvent.ACCOUNTS_CHANGED, [
                        userZkEvmEthAddress,
                    ]);
                    identify({
                        passportId: user.profile.sub,
                    });
                    this.#callSessionActivity(userZkEvmEthAddress);
                    return [userZkEvmEthAddress];
                }
                catch (error) {
                    if (error instanceof Error) {
                        trackError('passport', 'ethRequestAccounts', error);
                    }
                    flow.addEvent('errored');
                    throw error;
                }
                finally {
                    flow.addEvent('End');
                }
            }
            case 'eth_sendTransaction': {
                const zkEvmAddress = await this.#getZkEvmAddress();
                if (!zkEvmAddress) {
                    throw new JsonRpcError(ProviderErrorCode.UNAUTHORIZED, 'Unauthorised - call eth_requestAccounts first');
                }
                const flow = trackFlow('passport', 'ethSendTransaction');
                try {
                    return await this.#guardianClient.withConfirmationScreen({
                        width: 480,
                        height: 720,
                    })(async () => {
                        const ethSigner = await this.#getSigner();
                        flow.addEvent('endGetSigner');
                        return await sendTransaction({
                            params: request.params || [],
                            ethSigner,
                            guardianClient: this.#guardianClient,
                            rpcProvider: this.#rpcProvider,
                            relayerClient: this.#relayerClient,
                            zkEvmAddress,
                            flow,
                        });
                    });
                }
                catch (error) {
                    if (error instanceof Error) {
                        trackError('passport', 'eth_sendTransaction', error);
                    }
                    flow.addEvent('errored');
                    throw error;
                }
                finally {
                    flow.addEvent('End');
                }
            }
            case 'eth_accounts': {
                const zkEvmAddress = await this.#getZkEvmAddress();
                return zkEvmAddress ? [zkEvmAddress] : [];
            }
            case 'personal_sign': {
                const zkEvmAddress = await this.#getZkEvmAddress();
                if (!zkEvmAddress) {
                    throw new JsonRpcError(ProviderErrorCode.UNAUTHORIZED, 'Unauthorised - call eth_requestAccounts first');
                }
                const flow = trackFlow('passport', 'personalSign');
                try {
                    return await this.#guardianClient.withConfirmationScreen({
                        width: 480,
                        height: 720,
                    })(async () => {
                        const ethSigner = await this.#getSigner();
                        flow.addEvent('endGetSigner');
                        if (this.#config.forceScwDeployBeforeMessageSignature) {
                            // Check if the smart contract wallet has been deployed
                            const nonce = await getNonce(this.#rpcProvider, zkEvmAddress);
                            if (!nonce.gt(0)) {
                                // If the smart contract wallet has not been deployed,
                                // submit a transaction before signing the message
                                return await sendDeployTransactionAndPersonalSign({
                                    params: request.params || [],
                                    ethSigner,
                                    zkEvmAddress,
                                    rpcProvider: this.#rpcProvider,
                                    guardianClient: this.#guardianClient,
                                    relayerClient: this.#relayerClient,
                                    flow,
                                });
                            }
                        }
                        return await personalSign({
                            params: request.params || [],
                            ethSigner,
                            zkEvmAddress,
                            rpcProvider: this.#rpcProvider,
                            guardianClient: this.#guardianClient,
                            relayerClient: this.#relayerClient,
                            flow,
                        });
                    });
                }
                catch (error) {
                    if (error instanceof Error) {
                        trackError('passport', 'personal_sign', error);
                    }
                    flow.addEvent('errored');
                    throw error;
                }
                finally {
                    flow.addEvent('End');
                }
            }
            case 'eth_signTypedData':
            case 'eth_signTypedData_v4': {
                const zkEvmAddress = await this.#getZkEvmAddress();
                if (!zkEvmAddress) {
                    throw new JsonRpcError(ProviderErrorCode.UNAUTHORIZED, 'Unauthorised - call eth_requestAccounts first');
                }
                const flow = trackFlow('passport', 'ethSignTypedDataV4');
                try {
                    return await this.#guardianClient.withConfirmationScreen({
                        width: 480,
                        height: 720,
                    })(async () => {
                        const ethSigner = await this.#getSigner();
                        flow.addEvent('endGetSigner');
                        return await signTypedDataV4({
                            method: request.method,
                            params: request.params || [],
                            ethSigner,
                            rpcProvider: this.#rpcProvider,
                            relayerClient: this.#relayerClient,
                            guardianClient: this.#guardianClient,
                            flow,
                        });
                    });
                }
                catch (error) {
                    if (error instanceof Error) {
                        trackError('passport', 'eth_signTypedData', error);
                    }
                    flow.addEvent('errored');
                    throw error;
                }
                finally {
                    flow.addEvent('End');
                }
            }
            case 'eth_chainId': {
                // Call detect network to fetch the chainId so to take advantage of
                // the caching layer provided by StaticJsonRpcProvider.
                // In case Passport is changed from StaticJsonRpcProvider to a
                // JsonRpcProvider, this function will still work as expected given
                // that detectNetwork call _uncachedDetectNetwork which will force
                // the provider to re-fetch the chainId from remote.
                const { chainId } = await this.#rpcProvider.detectNetwork();
                return utils$1.hexlify(chainId);
            }
            // Pass through methods
            case 'eth_getBalance':
            case 'eth_getCode':
            case 'eth_getTransactionCount': {
                const [address, blockNumber] = request.params || [];
                return this.#rpcProvider.send(request.method, [
                    address,
                    blockNumber || 'latest',
                ]);
            }
            case 'eth_getStorageAt': {
                const [address, storageSlot, blockNumber] = request.params || [];
                return this.#rpcProvider.send(request.method, [
                    address,
                    storageSlot,
                    blockNumber || 'latest',
                ]);
            }
            case 'eth_call':
            case 'eth_estimateGas': {
                const [transaction, blockNumber] = request.params || [];
                return this.#rpcProvider.send(request.method, [
                    transaction,
                    blockNumber || 'latest',
                ]);
            }
            case 'eth_gasPrice':
            case 'eth_blockNumber':
            case 'eth_getBlockByHash':
            case 'eth_getBlockByNumber':
            case 'eth_getTransactionByHash':
            case 'eth_getTransactionReceipt': {
                return this.#rpcProvider.send(request.method, request.params || []);
            }
            case 'im_signEjectionTransaction': {
                const zkEvmAddress = await this.#getZkEvmAddress();
                if (!zkEvmAddress) {
                    throw new JsonRpcError(ProviderErrorCode.UNAUTHORIZED, 'Unauthorised - call eth_requestAccounts first');
                }
                const flow = trackFlow('passport', 'imSignEjectionTransaction');
                try {
                    const ethSigner = await this.#getSigner();
                    flow.addEvent('endGetSigner');
                    return await signEjectionTransaction({
                        params: request.params || [],
                        ethSigner,
                        zkEvmAddress,
                        flow,
                    });
                }
                catch (error) {
                    if (error instanceof Error) {
                        trackError('passport', 'imSignEjectionTransaction', error);
                    }
                    flow.addEvent('errored');
                    throw error;
                }
                finally {
                    flow.addEvent('End');
                }
            }
            default: {
                throw new JsonRpcError(ProviderErrorCode.UNSUPPORTED_METHOD, 'Method not supported');
            }
        }
    }
    async #performJsonRpcRequest(request) {
        const { id, jsonrpc } = request;
        try {
            const result = await this.#performRequest(request);
            return {
                id,
                jsonrpc,
                result,
            };
        }
        catch (error) {
            let jsonRpcError;
            if (error instanceof JsonRpcError) {
                jsonRpcError = error;
            }
            else if (error instanceof Error) {
                jsonRpcError = new JsonRpcError(RpcErrorCode.INTERNAL_ERROR, error.message);
            }
            else {
                jsonRpcError = new JsonRpcError(RpcErrorCode.INTERNAL_ERROR, 'Internal error');
            }
            return {
                id,
                jsonrpc,
                error: jsonRpcError,
            };
        }
    }
    async request(request) {
        try {
            return this.#performRequest(request);
        }
        catch (error) {
            if (error instanceof JsonRpcError) {
                throw error;
            }
            if (error instanceof Error) {
                throw new JsonRpcError(RpcErrorCode.INTERNAL_ERROR, error.message);
            }
            throw new JsonRpcError(RpcErrorCode.INTERNAL_ERROR, 'Internal error');
        }
    }
    sendAsync(request, callback) {
        if (!callback) {
            throw new Error('No callback provided');
        }
        if (Array.isArray(request)) {
            Promise.all(request.map(this.#performJsonRpcRequest))
                .then((result) => {
                callback(null, result);
            })
                .catch((error) => {
                callback(error, []);
            });
        }
        else {
            this.#performJsonRpcRequest(request)
                .then((result) => {
                callback(null, result);
            })
                .catch((error) => {
                callback(error, null);
            });
        }
    }
    async send(request, callbackOrParams, callback) {
        // Web3 >= 1.0.0-beta.38 calls `send` with method and parameters.
        if (typeof request === 'string') {
            if (typeof callbackOrParams === 'function') {
                return this.sendAsync({
                    method: request,
                    params: [],
                }, callbackOrParams);
            }
            if (callback) {
                return this.sendAsync({
                    method: request,
                    params: Array.isArray(callbackOrParams) ? callbackOrParams : [],
                }, callback);
            }
            return this.request({
                method: request,
                params: Array.isArray(callbackOrParams) ? callbackOrParams : [],
            });
        }
        // Web3 <= 1.0.0-beta.37 uses `send` with a callback for async queries.
        if (typeof callbackOrParams === 'function') {
            return this.sendAsync(request, callbackOrParams);
        }
        if (!Array.isArray(request) && typeof request === 'object') {
            return this.#performJsonRpcRequest(request);
        }
        throw new JsonRpcError(RpcErrorCode.INVALID_REQUEST, 'Invalid request');
    }
    on(event, listener) {
        this.#providerEventEmitter.on(event, listener);
    }
    removeListener(event, listener) {
        this.#providerEventEmitter.removeListener(event, listener);
    }
}

const passportProviderInfo = {
    // eslint-disable-next-line max-len
    icon: 'data:image/svg+xml,<svg viewBox="0 0 48 48" class="SvgIcon undefined Logo Logo--PassportSymbolOutlined css-1dn9atd" xmlns="http://www.w3.org/2000/svg"><g data-testid="undefined__g"><circle cx="24" cy="24" r="22.5" fill="url(%23paint0_radial_6324_83922)"></circle><circle cx="24" cy="24" r="22.5" fill="url(%23paint1_radial_6324_83922)"></circle><path d="M24 0C10.7452 0 0 10.7452 0 24C0 37.2548 10.7452 48 24 48C37.2548 48 48 37.2548 48 24C48 10.7452 37.2548 0 24 0ZM23.0718 9.16608C23.7383 8.83951 24.4406 8.86188 25.087 9.2287C27.3282 10.5059 29.5627 11.7942 31.786 13.096C32.5018 13.5165 32.8686 14.1897 32.8708 15.0173C32.8843 17.9184 32.8798 20.8171 32.8708 23.7182C32.8708 23.8255 32.8015 23.9821 32.7143 24.0335C31.8531 24.548 30.9808 25.0423 30.0347 25.5881V25.1318C30.0347 22.148 30.0257 19.1664 30.0414 16.1827C30.0436 15.6101 29.8468 15.241 29.339 14.9525C26.7377 13.474 24.1499 11.9687 21.5575 10.4723C21.4457 10.4075 21.3361 10.3381 21.1661 10.2352C21.8326 9.85722 22.4321 9.47698 23.0673 9.16608H23.0718ZM22.5953 38.8451C22.45 38.7713 22.3426 38.7198 22.2375 38.6595C18.8041 36.68 15.3752 34.687 11.9307 32.7232C10.9644 32.173 10.5238 31.3879 10.5349 30.2852C10.5551 27.9411 10.5484 25.597 10.5372 23.2507C10.5327 22.1927 10.9622 21.4255 11.8926 20.8977C14.3105 19.5221 16.715 18.1264 19.1195 16.7284C19.3275 16.6076 19.4796 16.5875 19.6965 16.7172C20.5264 17.216 21.3719 17.6924 22.2554 18.2024C22.0876 18.3031 21.9601 18.3791 21.8304 18.4552C19.2268 19.9582 16.6278 21.4658 14.0175 22.9599C13.5903 23.2037 13.3912 23.5213 13.3957 24.0179C13.4091 25.8654 13.4114 27.713 13.3957 29.5605C13.3912 30.0705 13.5948 30.3948 14.0332 30.6453C16.7866 32.2199 19.5288 33.8125 22.28 35.3916C22.5126 35.5258 22.611 35.6645 22.6065 35.9418C22.5864 36.888 22.5998 37.8363 22.5998 38.8473L22.5953 38.8451ZM22.5953 33.553C22.356 33.4166 22.1838 33.3204 22.0116 33.2198C19.8285 31.9605 17.6477 30.6967 15.4602 29.4464C15.2231 29.3122 15.1359 29.1668 15.1381 28.8917C15.1538 27.4714 15.1471 26.0511 15.1426 24.6308C15.1426 24.4384 15.1717 24.3064 15.3618 24.1991C16.167 23.7495 16.9633 23.2798 17.7618 22.8212C17.8199 22.7877 17.8826 22.7631 17.9877 22.7116V24.3064C17.9877 25.1698 18.0011 26.0354 17.9832 26.8988C17.972 27.3909 18.1622 27.7241 18.5916 27.9657C19.8285 28.6636 21.0498 29.3883 22.2867 30.0839C22.5305 30.2203 22.6043 30.3724 22.5998 30.6408C22.5842 31.5847 22.5931 32.5308 22.5931 33.5508L22.5953 33.553ZM20.0746 14.91C19.6116 14.6371 19.2157 14.6393 18.7527 14.91C16.1581 16.4265 13.5523 17.9228 10.9487 19.4259C10.8391 19.4908 10.7251 19.5489 10.5305 19.6541C10.5998 18.6654 10.3873 17.7327 10.7251 16.8291C10.9085 16.3348 11.2529 15.9635 11.7092 15.6995C13.8811 14.4447 16.0507 13.1877 18.227 11.9396C19.0211 11.4833 19.8308 11.4945 20.6248 11.953C23.0964 13.3756 25.5657 14.8026 28.0306 16.2341C28.1357 16.2945 28.2677 16.4309 28.2677 16.5338C28.2856 17.5493 28.2788 18.567 28.2788 19.6563C27.3819 19.1396 26.5543 18.6609 25.7267 18.1823C23.8412 17.093 21.9512 16.0149 20.0746 14.91ZM37.4427 30.8779C37.3778 31.6764 36.9103 32.2423 36.2192 32.6404C33.5732 34.1614 30.9294 35.6913 28.2856 37.2168C27.4557 37.6954 26.6259 38.1741 25.7938 38.6527C25.6932 38.7109 25.5903 38.7601 25.4539 38.8317C25.4449 38.693 25.4337 38.5924 25.4337 38.4917C25.4337 37.6149 25.4382 36.7404 25.4293 35.8636C25.4293 35.6645 25.4762 35.5437 25.6596 35.4386C29.5157 33.2198 33.3696 30.9942 37.2212 28.7709C37.2794 28.7374 37.3443 28.7105 37.4539 28.6591C37.4539 29.4375 37.4986 30.1622 37.4427 30.8779ZM37.4628 25.3577C37.4561 26.2658 36.9663 26.9033 36.1901 27.3506C33.175 29.0841 30.1622 30.8265 27.1493 32.5666C26.5991 32.8842 26.0466 33.1996 25.4561 33.5396C25.4472 33.3897 25.436 33.2913 25.436 33.1907C25.436 32.3273 25.4449 31.4617 25.4293 30.5983C25.4248 30.3523 25.5075 30.2226 25.72 30.0995C28.46 28.5271 31.1911 26.9368 33.9355 25.3733C34.4231 25.096 34.6378 24.7538 34.6334 24.1812C34.6132 21.1974 34.6244 18.2136 34.6244 15.2298V14.7087C35.3402 15.1404 36.0112 15.496 36.624 15.9299C37.1832 16.3258 37.465 16.9253 37.4673 17.6164C37.4762 20.1976 37.4829 22.7788 37.465 25.3599L37.4628 25.3577Z" fill="%230D0D0D"></path><path fill-rule="evenodd" d="M24 0C10.7452 0 0 10.7452 0 24C0 37.2548 10.7452 48 24 48C37.2548 48 48 37.2548 48 24C48 10.7452 37.2548 0 24 0ZM24 2C11.8497 2 2 11.8497 2 24C2 36.1503 11.8497 46 24 46C36.1503 46 46 36.1503 46 24C46 11.8497 36.1503 2 24 2Z" fill="url(%23paint2_radial_6324_83922)"></path><path fill-rule="evenodd" d="M24 0C10.7452 0 0 10.7452 0 24C0 37.2548 10.7452 48 24 48C37.2548 48 48 37.2548 48 24C48 10.7452 37.2548 0 24 0ZM24 2C11.8497 2 2 11.8497 2 24C2 36.1503 11.8497 46 24 46C36.1503 46 46 36.1503 46 24C46 11.8497 36.1503 2 24 2Z" fill="url(%23paint3_radial_6324_83922)"></path><defs><radialGradient id="paint0_radial_6324_83922" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(13.4442 13.3899) rotate(44.9817) scale(46.7487 99.1435)"><stop stop-color="%23A3EEF8"></stop><stop offset="0.177083" stop-color="%23A4DCF5"></stop><stop offset="0.380208" stop-color="%23A6AEEC"></stop><stop offset="1" stop-color="%23ECBEE1"></stop></radialGradient><radialGradient id="paint1_radial_6324_83922" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(25.9515 43.7068) rotate(84.265) scale(24.2138 46.3215)"><stop stop-color="%23FCF5EE"></stop><stop offset="0.715135" stop-color="%23ECBEE1" stop-opacity="0"></stop></radialGradient><radialGradient id="paint2_radial_6324_83922" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(12.7405 12.6825) rotate(44.9817) scale(49.8653 105.753)"><stop stop-color="%23A3EEF8"></stop><stop offset="0.177083" stop-color="%23A4DCF5"></stop><stop offset="0.380208" stop-color="%23A6AEEC"></stop><stop offset="1" stop-color="%23ECBEE1"></stop></radialGradient><radialGradient id="paint3_radial_6324_83922" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(26.0816 45.0206) rotate(84.265) scale(25.828 49.4096)"><stop stop-color="%23FCF5EE"></stop><stop offset="0.715135" stop-color="%23ECBEE1" stop-opacity="0"></stop></radialGradient></defs></g></svg>',
    name: 'Immutable Passport',
    rdns: 'com.immutable.passport',
    uuid: v4(),
};
function announceProvider(detail) {
    if (typeof window === 'undefined')
        return;
    const event = new CustomEvent('eip6963:announceProvider', { detail: Object.freeze(detail) });
    window.dispatchEvent(event);
    const handler = () => window.dispatchEvent(event);
    window.addEventListener('eip6963:requestProvider', handler);
}

const buildImxClientConfig = (passportModuleConfiguration) => {
    if (passportModuleConfiguration.overrides) {
        return createConfig({ basePath: passportModuleConfiguration.overrides.imxPublicApiDomain });
    }
    if (passportModuleConfiguration.baseConfig.environment === Environment.SANDBOX) {
        return imxApiConfig.getSandbox();
    }
    return imxApiConfig.getProduction();
};
const buildImxApiClients = (passportModuleConfiguration) => {
    if (passportModuleConfiguration.overrides?.imxApiClients)
        return passportModuleConfiguration.overrides.imxApiClients;
    const config = buildImxClientConfig(passportModuleConfiguration);
    return new ImxApiClients(config);
};
const buildPrivateVars = (passportModuleConfiguration) => {
    const config = new PassportConfiguration(passportModuleConfiguration);
    const authManager = new AuthManager(config);
    const magicAdapter = new MagicAdapter(config);
    const confirmationScreen = new ConfirmationScreen(config);
    const multiRollupApiClients = new MultiRollupApiClients(config.multiRollupConfig);
    const passportEventEmitter = new TypedEventEmitter();
    const immutableXClient = passportModuleConfiguration.overrides
        ? passportModuleConfiguration.overrides.immutableXClient
        : new IMXClient({ baseConfig: passportModuleConfiguration.baseConfig });
    const guardianClient = new GuardianClient({
        confirmationScreen,
        config,
        authManager,
        guardianApi: multiRollupApiClients.guardianApi,
    });
    const imxApiClients = buildImxApiClients(passportModuleConfiguration);
    const passportImxProviderFactory = new PassportImxProviderFactory({
        authManager,
        immutableXClient,
        magicAdapter,
        passportEventEmitter,
        imxApiClients,
        guardianClient,
    });
    return {
        config,
        authManager,
        magicAdapter,
        confirmationScreen,
        immutableXClient,
        multiRollupApiClients,
        passportEventEmitter,
        passportImxProviderFactory,
        guardianClient,
    };
};
class Passport {
    authManager;
    config;
    confirmationScreen;
    immutableXClient;
    magicAdapter;
    multiRollupApiClients;
    passportImxProviderFactory;
    passportEventEmitter;
    guardianClient;
    constructor(passportModuleConfiguration) {
        const privateVars = buildPrivateVars(passportModuleConfiguration);
        this.config = privateVars.config;
        this.authManager = privateVars.authManager;
        this.magicAdapter = privateVars.magicAdapter;
        this.confirmationScreen = privateVars.confirmationScreen;
        this.immutableXClient = privateVars.immutableXClient;
        this.multiRollupApiClients = privateVars.multiRollupApiClients;
        this.passportEventEmitter = privateVars.passportEventEmitter;
        this.passportImxProviderFactory = privateVars.passportImxProviderFactory;
        this.guardianClient = privateVars.guardianClient;
        setPassportClientId(passportModuleConfiguration.clientId);
        track('passport', 'initialise');
    }
    /**
     * @deprecated The method `login` with an argument of `{ useCachedSession: true }` should be used in conjunction with
     * `connectImx` instead.
     */
    async connectImxSilent() {
        return withMetricsAsync(() => this.passportImxProviderFactory.getProviderSilent(), 'connectImxSilent');
    }
    async connectImx() {
        return withMetricsAsync(() => this.passportImxProviderFactory.getProvider(), 'connectImx');
    }
    connectEvm(options = {
        announceProvider: true,
    }) {
        return withMetrics(() => {
            const provider = new ZkEvmProvider({
                passportEventEmitter: this.passportEventEmitter,
                authManager: this.authManager,
                magicAdapter: this.magicAdapter,
                config: this.config,
                multiRollupApiClients: this.multiRollupApiClients,
                guardianClient: this.guardianClient,
            });
            if (options?.announceProvider) {
                announceProvider({
                    info: passportProviderInfo,
                    provider,
                });
            }
            return provider;
        }, 'connectEvm');
    }
    /**
     *
     * Initiates the authorisation flow.
     *
     * @param options.useCachedSession = false - If true, and no active session exists, then the user will not be
     * prompted to log in and the Promise will resolve with a null value.
     * @param options.anonymousId - If provided, Passport internal metrics will be enriched with this value.
     * @param options.useSilentLogin - If true, and no active session exists, then the user will not be prompted to log in. Instead, we will attempt to authenticate the user silently. This approach will fail if the user does not have an active session with the authentication server, or if user input is required (for example, consent is required).
     * @returns {Promise<UserProfile | null>} the user profile if the user is logged in, otherwise null
     */
    async login(options) {
        return withMetricsAsync(async () => {
            const { useCachedSession = false, useSilentLogin } = options || {};
            let user = null;
            try {
                user = await this.authManager.getUser();
            }
            catch (error) {
                if (error instanceof Error) {
                    trackError('passport', 'login', error);
                }
                if (useCachedSession) {
                    throw error;
                }
                logger.warn('Failed to retrieve a cached user session', error);
            }
            if (!user && useSilentLogin) {
                user = await this.authManager.forceUserRefresh();
            }
            else if (!user && !useCachedSession) {
                user = await this.authManager.login(options?.anonymousId);
            }
            if (user) {
                identify({
                    passportId: user.profile.sub,
                });
                this.passportEventEmitter.emit(PassportEvents.LOGGED_IN, user);
            }
            return user ? user.profile : null;
        }, 'login');
    }
    async loginCallback() {
        return withMetricsAsync(() => this.authManager.loginCallback(), 'loginCallback');
    }
    async loginWithDeviceFlow(options) {
        return withMetricsAsync(() => this.authManager.loginWithDeviceFlow(options?.anonymousId), 'loginWithDeviceFlow');
    }
    async loginWithDeviceFlowCallback(deviceCode, interval, timeoutMs) {
        return withMetricsAsync(async () => {
            const user = await this.authManager.loginWithDeviceFlowCallback(deviceCode, interval, timeoutMs);
            this.passportEventEmitter.emit(PassportEvents.LOGGED_IN, user);
            return user.profile;
        }, 'loginWithDeviceFlowCallback');
    }
    loginWithPKCEFlow() {
        return withMetrics(() => this.authManager.getPKCEAuthorizationUrl(), 'loginWithPKCEFlow');
    }
    async loginWithPKCEFlowCallback(authorizationCode, state) {
        return withMetricsAsync(async () => {
            const user = await this.authManager.loginWithPKCEFlowCallback(authorizationCode, state);
            this.passportEventEmitter.emit(PassportEvents.LOGGED_IN, user);
            return user.profile;
        }, 'loginWithPKCEFlowCallback');
    }
    async logout() {
        return withMetricsAsync(async () => {
            if (this.config.oidcConfiguration.logoutMode === 'silent') {
                await Promise.allSettled([
                    this.authManager.logout(),
                    this.magicAdapter.logout(),
                ]);
            }
            else {
                // We need to ensure that the Magic wallet is logged out BEFORE redirecting
                await this.magicAdapter.logout();
                await this.authManager.logout();
            }
            this.passportEventEmitter.emit(PassportEvents.LOGGED_OUT);
        }, 'logout');
    }
    /**
     * Logs the user out of Passport when using device flow authentication.
     *
     * @returns {Promise<string>} The device flow end session endpoint. Consumers are responsible for
     * opening this URL in the same browser that was used to log the user in.
     */
    async logoutDeviceFlow() {
        return withMetricsAsync(async () => {
            await this.authManager.removeUser();
            await this.magicAdapter.logout();
            this.passportEventEmitter.emit(PassportEvents.LOGGED_OUT);
            return await this.authManager.getDeviceFlowEndSessionEndpoint();
        }, 'logoutDeviceFlow');
    }
    /**
     * This method should only be called from the logout redirect uri
     * when logout mode is 'silent'.
     */
    async logoutSilentCallback(url) {
        return withMetricsAsync(() => this.authManager.logoutSilentCallback(url), 'logoutSilentCallback');
    }
    async getUserInfo() {
        return withMetricsAsync(async () => {
            const user = await this.authManager.getUser();
            return user?.profile;
        }, 'getUserInfo');
    }
    async getIdToken() {
        return withMetricsAsync(async () => {
            const user = await this.authManager.getUser();
            return user?.idToken;
        }, 'getIdToken');
    }
    async getAccessToken() {
        return withMetricsAsync(async () => {
            const user = await this.authManager.getUser();
            return user?.accessToken;
        }, 'getAccessToken');
    }
    async getLinkedAddresses() {
        return withMetricsAsync(async () => {
            const user = await this.authManager.getUser();
            if (!user?.profile.sub) {
                return [];
            }
            const headers = { Authorization: `Bearer ${user.accessToken}` };
            const getUserInfoResult = await this.multiRollupApiClients.passportProfileApi.getUserInfo({ headers });
            return getUserInfoResult.data.linked_addresses;
        }, 'getLinkedAddresses');
    }
    async linkExternalWallet(params) {
        const flow = trackFlow('passport', 'linkExternalWallet');
        const user = await this.authManager.getUser();
        if (!user) {
            throw new PassportError('User is not logged in', PassportErrorType.NOT_LOGGED_IN_ERROR);
        }
        const isRegisteredWithIMX = isUserImx(user);
        const isRegisteredWithZkEvm = isUserZkEvm(user);
        if (!isRegisteredWithIMX && !isRegisteredWithZkEvm) {
            throw new PassportError('User has not been registered', PassportErrorType.USER_NOT_REGISTERED_ERROR);
        }
        const headers = { Authorization: `Bearer ${user.accessToken}` };
        const linkWalletV2Request = {
            type: params.type,
            wallet_address: params.walletAddress,
            signature: params.signature,
            nonce: params.nonce,
        };
        try {
            const linkWalletV2Result = await this.multiRollupApiClients
                .passportProfileApi.linkWalletV2({ linkWalletV2Request }, { headers });
            return { ...linkWalletV2Result.data };
        }
        catch (error) {
            if (error instanceof Error) {
                trackError('passport', 'linkExternalWallet', error);
            }
            flow.addEvent('errored');
            if (isAxiosError(error) && error.response) {
                if (error.response.data && isAPIError(error.response.data)) {
                    const { code, message } = error.response.data;
                    switch (code) {
                        case 'ALREADY_LINKED':
                            throw new PassportError(message, PassportErrorType.LINK_WALLET_ALREADY_LINKED_ERROR);
                        case 'MAX_WALLETS_LINKED':
                            throw new PassportError(message, PassportErrorType.LINK_WALLET_MAX_WALLETS_LINKED_ERROR);
                        case 'DUPLICATE_NONCE':
                            throw new PassportError(message, PassportErrorType.LINK_WALLET_DUPLICATE_NONCE_ERROR);
                        case 'VALIDATION_ERROR':
                            throw new PassportError(message, PassportErrorType.LINK_WALLET_VALIDATION_ERROR);
                        default:
                            throw new PassportError(message, PassportErrorType.LINK_WALLET_GENERIC_ERROR);
                    }
                }
                else if (error.response.status) {
                    // Handle unexpected error with a generic error message
                    throw new PassportError(`Link wallet request failed with status code ${error.response.status}`, PassportErrorType.LINK_WALLET_GENERIC_ERROR);
                }
            }
            let message = 'Link wallet request failed';
            if (error instanceof Error) {
                message += `: ${error.message}`;
            }
            throw new PassportError(message, PassportErrorType.LINK_WALLET_GENERIC_ERROR);
        }
        finally {
            flow.addEvent('End');
        }
    }
}

var passport = /*#__PURE__*/Object.freeze({
  __proto__: null,
  JsonRpcError: JsonRpcError,
  Passport: Passport,
  PassportError: PassportError,
  get ProviderErrorCode () { return ProviderErrorCode; },
  get ProviderEvent () { return ProviderEvent; },
  get RpcErrorCode () { return RpcErrorCode; }
});

export { JsonRpcError as J, Passport as P, RpcErrorCode as R, PassportError as a, ProviderErrorCode as b, ProviderEvent as c, passport as p };
